import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary,
  Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress,
  Divider, FormControl, FormControlLabel, Grid,
  IconButton, InputAdornment, InputLabel, ListItemIcon, ListItemText,
  Dialog, DialogActions, DialogContent, DialogTitle,
  Menu, MenuItem, Paper, Popover,
  Select, Skeleton, Snackbar, Switch, Tab, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TablePagination,
  Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Add, Archive, ContentCopy, Edit, ExpandMore,
  Refresh, Search, ArrowBack, Save, FileDownloadOutlined, Visibility as ViewIcon, Close,
  CheckCircle, RadioButtonUnchecked, Delete, LayersOutlined,
  TuneRounded,
  KeyboardArrowDown, WarningAmberRounded,
  InfoOutlined, AttachMoneyOutlined,
  TuneOutlined, LockOutlined, ExtensionOutlined,
  DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material';
import { usePermissions } from '../../../../contexts/PermissionContext';
import * as api from '../../../../services/subscriptionAPI';

// ─── constants ─────────────────────────────────────────────────────────────

const BILLING_PERIODS = ['monthly', 'yearly', 'one_time'];
const PERIOD_LABELS   = {
  monthly: 'Monthly', yearly: 'Yearly', one_time: 'One-time',
};

// Restriction defaults — populated after fetching from DB (empty = no restriction data loaded yet)
const EMPTY_RESTRICTIONS = {
  category_ids:       [],
  model_type_ids:     [],
  output_format_ids:  [],
  media_quality_ids:  [],
  ethnicity_ids:      [],
  clothing_type_ids:  [],
  bust_finish_ids:    [],
  bust_color_ids:     [],
};

/** Merge saved plan restrictions with defaults so new dimensions default to all-selected. */
function mergeSavedRestrictions(saved, defaults) {
  const merged = { ...defaults };
  if (!saved) return merged;
  for (const key of Object.keys(defaults)) {
    if (Array.isArray(saved[key])) merged[key] = saved[key];
  }
  return merged;
}

const _normModelLabel = (s) => (s || '').toLowerCase().replace(/[_-]/g, ' ').trim();

function _isBustDisplayModel(mt) {
  const name = _normModelLabel(mt.name);
  const label = _normModelLabel(mt.display_name);
  return name === 'bust' || label.includes('bust display');
}

function _isHumanModel(mt) {
  if (_isHumanHandModel(mt)) return false;
  const name = _normModelLabel(mt.name);
  const label = _normModelLabel(mt.display_name);
  return name === 'human model' || label === 'human model';
}

function _isHumanHandModel(mt) {
  const raw = `${_normModelLabel(mt.name)} ${_normModelLabel(mt.display_name)}`;
  return raw.includes('human hand');
}

function _isProductOnlyModel(mt) {
  const raw = `${_normModelLabel(mt.name)} ${_normModelLabel(mt.display_name)}`;
  return raw.includes('product only');
}

/** Keep dependent generation restrictions consistent (plan + addon editor). */
function applyRestrictionDependencies(restrictions, catalog) {
  const { modelTypes = [], outputFormats = [], mediaQualities = [] } = catalog || {};
  const next = { ...restrictions };

  const dropModelTypes = (predicate) => {
    const dropIds = new Set(modelTypes.filter(predicate).map((mt) => mt.id));
    if (!dropIds.size) return;
    next.model_type_ids = next.model_type_ids.filter((id) => !dropIds.has(id));
  };

  const hasAny = (ids) => Array.isArray(ids) && ids.length > 0;

  // No categories → no model types (generation requires a category)
  if (!hasAny(next.category_ids)) {
    next.model_type_ids = [];
  }

  // Bust Display needs at least one bust type and one bust color
  if (!hasAny(next.bust_finish_ids) || !hasAny(next.bust_color_ids)) {
    dropModelTypes(_isBustDisplayModel);
  }

  // Human Model / Human Hand need ethnicity and/or clothing options available
  if (!hasAny(next.ethnicity_ids) && !hasAny(next.clothing_type_ids)) {
    dropModelTypes((mt) => _isHumanModel(mt) || _isHumanHandModel(mt));
  }

  // Product Only is image-only in the merchant wizard
  const imageFormatIds = outputFormats
    .filter((of) => (of.name || '').toUpperCase() === 'IMAGE')
    .map((of) => of.id);
  const imageFormatAllowed = !imageFormatIds.length
    || imageFormatIds.some((id) => next.output_format_ids.includes(id));
  if (!imageFormatAllowed) {
    dropModelTypes(_isProductOnlyModel);
    // GIF output also depends on image pipeline
    const gifFormatIds = outputFormats
      .filter((of) => (of.name || '').toUpperCase() === 'GIF')
      .map((of) => of.id);
    if (gifFormatIds.length) {
      next.output_format_ids = next.output_format_ids.filter((id) => !gifFormatIds.includes(id));
    }
  }

  // Media quality scopes follow selected output formats
  const imageFormatSelected = imageFormatIds.some((id) => next.output_format_ids.includes(id));
  const videoFormatIds = outputFormats
    .filter((of) => (of.name || '').toUpperCase() === 'VIDEO')
    .map((of) => of.id);
  const videoFormatSelected = videoFormatIds.some((id) => next.output_format_ids.includes(id));

  if (!imageFormatSelected) {
    const imageMqIds = new Set(
      mediaQualities
        .filter((q) => (q.applies_to || 'IMAGE').toUpperCase() === 'IMAGE')
        .map((q) => q.id),
    );
    next.media_quality_ids = next.media_quality_ids.filter((id) => !imageMqIds.has(id));
  }
  if (!videoFormatSelected) {
    const videoMqIds = new Set(
      mediaQualities
        .filter((q) => (q.applies_to || '').toUpperCase() === 'VIDEO')
        .map((q) => q.id),
    );
    next.media_quality_ids = next.media_quality_ids.filter((id) => !videoMqIds.has(id));
  }

  return next;
}

const CARD_SX = {
  border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper',
};
const ACCORDION_SX = {
  ...CARD_SX, boxShadow: 'none', '&:before': { display: 'none' },
  '&.Mui-expanded': { borderColor: 'primary.light' },
};
const SortTH = ({ label, field, sortBy, sortDir, onSort, sx = {} }) => (
  <Box display="flex" alignItems="center" gap={0.5} sx={{ cursor: 'pointer', userSelect: 'none', ...sx }}
    onClick={() => onSort(field)}>
    {label}
    <Box sx={{ display: 'flex', flexDirection: 'column', ml: 0.25, opacity: sortBy === field ? 1 : 0.3 }}>
      <svg width="8" height="5" viewBox="0 0 8 5"><path d="M4 0L8 5H0Z" fill={sortBy === field && sortDir === 'asc' ? 'currentColor' : '#9ca3af'}/></svg>
      <svg width="8" height="5" viewBox="0 0 8 5"><path d="M4 5L0 0H8Z" fill={sortBy === field && sortDir === 'desc' ? 'currentColor' : '#9ca3af'}/></svg>
    </Box>
  </Box>
);

const TH = {
  fontWeight: 700, fontSize: 11, color: 'text.secondary',
  letterSpacing: 0.6, textTransform: 'uppercase', bgcolor: 'grey.50', py: 1.5,
  whiteSpace: 'nowrap',
};
const _BTN = { width: 30, height: 30, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' };
const BTN_PRIMARY = { ..._BTN, color: 'primary.main',   '&:hover': { bgcolor: 'primary.50',  borderColor: 'primary.200'  } };
const BTN_ERROR   = { ..._BTN, color: 'error.main',     '&:hover': { bgcolor: 'error.50',    borderColor: 'error.200'    } };
const BTN_GREY    = { ..._BTN, color: 'text.secondary', '&:hover': { bgcolor: 'grey.100',    borderColor: 'grey.300'     } };
const BTN_SUCCESS = { ..._BTN, color: 'success.main',   '&:hover': { bgcolor: 'success.50',  borderColor: 'success.200'  } };
// Larger checkbox used throughout the editor forms
const CB_SX = { '& .MuiSvgIcon-root': { fontSize: 22 } };

// ─── small helpers ──────────────────────────────────────────────────────────

function useSnack() {
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const show  = (msg, sev = 'success') => setSnack({ open: true, msg, severity: sev });
  const close = () => setSnack(s => ({ ...s, open: false }));
  return { snack, show, close };
}

function EmptyState({ message, sub }) {
  return (
    <Box display="flex" flexDirection="column" alignItems="center" py={10} gap={2}>
      <Box sx={{ width: 72, height: 72, borderRadius: '50%', bgcolor: 'grey.100',
        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <LayersOutlined sx={{ fontSize: 32, color: 'text.disabled' }} />
      </Box>
      <Box textAlign="center">
        <Typography variant="body1" fontWeight={600} color="text.secondary">{message}</Typography>
        {sub && <Typography variant="body2" color="text.disabled" mt={0.5}>{sub}</Typography>}
      </Box>
    </Box>
  );
}

const SECTION_ICONS = {
  'Basics':                   <InfoOutlined sx={{ fontSize: 15 }} />,
  'Pricing':                  <AttachMoneyOutlined sx={{ fontSize: 15 }} />,
  'Merchant Module Access':   <LockOutlined sx={{ fontSize: 15 }} />,
  'Plan Eligibility':         <ExtensionOutlined sx={{ fontSize: 15 }} />,
  'AI Generation Restrictions': <TuneOutlined sx={{ fontSize: 15 }} />,
};

function SectionHeader({ label, count }) {
  const icon = SECTION_ICONS[label];
  return (
    <Box display="flex" alignItems="center" gap={1}>
      {icon && (
        <Box sx={{ color: 'text.disabled', display: 'flex', alignItems: 'center' }}>
          {icon}
        </Box>
      )}
      <Typography fontWeight={700} fontSize={12} letterSpacing={0.8} color="text.secondary"
        sx={{ textTransform: 'uppercase' }}>
        {label}
      </Typography>
      {count != null && count > 0 && (
        <Chip label={count} size="small" color="primary"
          sx={{ height: 18, fontSize: 11, fontWeight: 700, '& .MuiChip-label': { px: 1 } }} />
      )}
    </Box>
  );
}

function StatusBadge({ status }) {
  const active = status === 1;
  return (
    <Box display="inline-flex" alignItems="center" gap={0.6}
      sx={{ px: 1.2, py: 0.3, borderRadius: 5,
        bgcolor: active ? 'success.50' : 'grey.100',
        border: '1px solid', borderColor: active ? 'success.200' : 'grey.300' }}>
      <Box sx={{ width: 6, height: 6, borderRadius: '50%',
        bgcolor: active ? 'success.main' : 'text.disabled' }} />
      <Typography variant="caption" fontWeight={600}
        color={active ? 'success.dark' : 'text.secondary'}>
        {active ? 'Active' : 'Archived'}
      </Typography>
    </Box>
  );
}

// ─── main component ─────────────────────────────────────────────────────────

export default function PlansManagement() {
  const { hasPermission } = usePermissions();
  const canView       = hasPermission('Plans & Add-ons', 'view');
  const canViewDetail = hasPermission('Plans & Add-ons', 'view_detail');
  const canCreate     = hasPermission('Plans & Add-ons', 'create');
  const canUpdate     = hasPermission('Plans & Add-ons', 'update');
  const canPublish    = hasPermission('Plans & Add-ons', 'publish');
  const canArchive    = hasPermission('Plans & Add-ons', 'archive');
  const canDuplicate  = hasPermission('Plans & Add-ons', 'duplicate');

  const [view, setView]       = useState('list');
  const [isAddon, setIsAddon] = useState(false);

  // unified list
  const [items, setItems]               = useState([]);
  const [total, setTotal]               = useState(0);
  const [page, setPage]                 = useState(0);
  const [perPage, setPerPage]           = useState(10);
  const [sortBy, setSortBy]             = useState('sort_order');
  const [sortDir, setSortDir]           = useState('asc');
  const [allPlans, setAllPlans]         = useState([]);
  const [listLoading, setListLoading]   = useState(false);
  const [searchInput, setSearchInput]   = useState('');   // raw input (debounced → search)
  const [search, setSearch]             = useState('');   // debounced value sent to API
  const [filterType, setFilterType]     = useState('all');
  const [filterStatus, setFilterStatus] = useState('active');
  const [filterVisibility, setFilterVisibility] = useState('all');
  const searchDebounceTimer = useRef(null);
  const planCodeTouched = useRef(false);

  // editor
  const [editingId, setEditingId]         = useState(null);
  const [saving, setSaving]               = useState(false);
  const [editorLoading, setEditorLoading] = useState(false);
  const [planErrors, setPlanErrors]       = useState({});   // TC02/TC03/TC04 field errors
  const [addonErrors, setAddonErrors]     = useState({});
  const [isDirtyPricing, setIsDirtyPricing] = useState(false);
  const [isDirtyRbac, setIsDirtyRbac]       = useState(false);
  const [featureLabels, setFeatureLabels]     = useState([]);  // [{id?, plan_id, module_id, label, sort_order}]
  const [isDirtyLabels, setIsDirtyLabels]     = useState(false);
  const dragLabelIdx                          = React.useRef(null);
  const [pricingErrors, setPricingErrors]   = useState({});  // key: "currency/period" → error string
  const [rbacErrors, setRbacErrors]         = useState({});  // key: action_id → error string
  const [backConfirm, setBackConfirm]             = useState(false);
  const [removeCurrencyConfirm, setRemoveCurrencyConfirm] = useState(null);

  // plan form
  const [planForm, setPlanForm] = useState({
    plan_code: '', display_name: '', description: '',
    product_type: 'subscription', visibility: 'public',
    show_in_merchant_catalog: true, sales_model: 'self_serve', sort_order: 0, plan_tier: 0,
  });
  const [currencies, setCurrencies]           = useState([]);
  const [taxRules, setTaxRules]               = useState([]);
  const [pricingGrid, setPricingGrid]         = useState({});
  const [merchantModules, setMerchantModules] = useState([]);
  const [planRbac, setPlanRbac]               = useState({});
  const [planRbacLimits, setPlanRbacLimits]   = useState({});
  const [draftVersion, setDraftVersion]       = useState(null);
  const [publishedVersion, setPublishedVersion] = useState(null);
  // AI restriction options (from DB)
  const [aiCategories, setAiCategories]       = useState([]);
  const [aiModelTypes, setAiModelTypes]       = useState([]);
  const [aiOutputFormats, setAiOutputFormats] = useState([]);
  const [aiMediaQualities, setAiMediaQualities] = useState([]);
  const [aiEthnicities, setAiEthnicities]       = useState([]);
  const [aiClothingTypes, setAiClothingTypes]   = useState([]);
  const [aiBustFinishes, setAiBustFinishes]     = useState([]);
  const [aiBustColors, setAiBustColors]         = useState([]);

  const [planRestrictions, setPlanRestrictions] = useState({ ...EMPTY_RESTRICTIONS });

  // addon form
  const [addonForm, setAddonForm] = useState({
    addon_code: '', display_name: '', description: '',
    addon_category: 'token_pack', visibility: 'public', sort_order: 0,
  });
  const [addonPricing, setAddonPricing]               = useState([]);
  const [addonRbac, setAddonRbac]                     = useState({});
  const [addonRbacLimits, setAddonRbacLimits]         = useState({});
  const [addonEligiblePlanIds, setAddonEligiblePlanIds] = useState([]);
  const [addonRestrictions, setAddonRestrictions] = useState({ ...EMPTY_RESTRICTIONS });

  // allowlist
  const [allowedMerchants, setAllowedMerchants]           = useState([]);
  const [merchantSearchResults, setMerchantSearchResults] = useState([]);
  const [merchantSearchLoading, setMerchantSearchLoading] = useState(false);
  const [selectedMerchantIds, setSelectedMerchantIds]     = useState(new Set());
  const merchantTimer   = useRef(null);
  const excelInputRef   = useRef(null);

  // add-new menu
  const [addAnchor, setAddAnchor] = useState(null);

  // publish
  const [publishAnchor, setPublishAnchor] = useState(null);
  const [publishScope, setPublishScope]   = useState('new_only');
  const [publishNotes, setPublishNotes]   = useState('');
  const [publishing, setPublishing]       = useState(false);

  // archive confirmation
  const [archiveConfirm, setArchiveConfirm] = useState({ open: false, item: null });
  const [viewDialog, setViewDialog]         = useState({ open: false, data: null, type: null, loading: false });
  const [viewTab,    setViewTab]            = useState(0);

  // view mode (read-only editor)
  const [isViewMode, setIsViewMode]                   = useState(false);
  const [allVersions, setAllVersions]                 = useState([]);
  const [selectedViewVersionIdx, setSelectedViewVersionIdx] = useState(0);
  const [versionDiff, setVersionDiff] = useState({ pricingKeys: new Set(), rbacActionIds: new Set(), vsVersionNumber: null });
  const [diffMode, setDiffMode]       = useState('previous'); // 'previous' | 'published'

  const openView = async (item, isPlan) => {
    setViewTab(0);
    setViewDialog({ open: true, data: null, type: isPlan ? 'plan' : 'addon', loading: true });
    try {
      const res = isPlan ? await api.getPlan(item.id) : await api.getAddon(item.id);
      setViewDialog({ open: true, data: res.data, type: isPlan ? 'plan' : 'addon', loading: false });
    } catch {
      setViewDialog(v => ({ ...v, loading: false }));
      show('Failed to load details', 'error');
    }
  };

  const { snack, show, close: closeSnack } = useSnack();

  // ── load unified list ───────────────────────────────────────────────────

  const loadItems = useCallback(async () => {
    setListLoading(true);
    try {
      const archived = filterStatus === 'archived' ? 'true' : 'false';
      const visParam = filterVisibility !== 'all' ? { visibility: filterVisibility } : {};

      // For combined 'all' view: fetch everything (plans typically < 200 total),
      // then slice client-side so pagination is accurate across both types.
      // For single-type view: use server-side pagination directly.
      const isAll = filterType === 'all';
      const sortParam = isAll
        ? { sort_by: sortBy, sort_dir: sortDir, per_page: 200 }
        : { sort_by: sortBy, sort_dir: sortDir, page: page + 1, per_page: perPage };

      const [planRes, addonRes] = await Promise.all([
        filterType === 'addon'
          ? Promise.resolve({ data: { items: [], total: 0 } })
          : api.getPlans({ search, include_archived: archived, ...visParam, ...sortParam }),
        filterType === 'plan'
          ? Promise.resolve({ data: { items: [], total: 0 } })
          : api.getAddons({ search, include_archived: archived, ...visParam, ...sortParam }),
      ]);

      const plans  = (planRes.data.items  || []).map(p => ({ ...p, _type: 'plan' }));
      const addons = (addonRes.data.items || []).map(a => ({ ...a, _type: 'addon' }));
      const planTotal  = planRes.data.total  || 0;
      const addonTotal = addonRes.data.total || 0;

      setAllPlans(plans);
      const combined = [...plans, ...addons].sort((a, b) =>
        (a.sort_order || 0) - (b.sort_order || 0) || (a.display_name || '').localeCompare(b.display_name || '')
      );
      setItems(combined);
      setTotal(filterType === 'plan' ? planTotal : filterType === 'addon' ? addonTotal : combined.length);
    } catch { show('Failed to load items', 'error'); }
    finally { setListLoading(false); }
  }, [search, filterStatus, filterType, filterVisibility, page, perPage, sortBy, sortDir]);

  // Reset page on filter/sort change
  useEffect(() => { setPage(0); }, [search, filterStatus, filterType, filterVisibility, sortBy, sortDir]);
  useEffect(() => { if (view === 'list') loadItems(); }, [view, loadItems]);

  // ── editor reference data ───────────────────────────────────────────────

  const loadEditorData = async () => {
    const [curR, trR, modR, catR, mtR, ofR, mqR, ethR, clothR, bustFR, bustCR] = await Promise.all([
      api.getCurrencies(), api.getTaxRules(), api.getMerchantModules(),
      api.getCategories(), api.getModelTypes(), api.getOutputFormats(), api.getMediaQualities(),
      api.getEthnicities(), api.getClothingTypes(), api.getBustFinishes(), api.getBustColors(),
    ]);
    setCurrencies(curR.data || []);
    setTaxRules(trR.data || []);
    setMerchantModules(modR.data || []);

    const cats  = catR.data?.data  || [];
    const mts   = mtR.data?.data   || [];
    const ofs   = ofR.data?.data   || [];
    const mqs   = mqR.data?.data   || [];
    const eths  = ethR.data?.data  || [];
    const cloth = clothR.data?.data || [];
    const bustF = bustFR.data?.data || [];
    const bustC = bustCR.data?.data || [];
    setAiCategories(cats);
    setAiModelTypes(mts);
    setAiOutputFormats(ofs);
    setAiMediaQualities(mqs);
    setAiEthnicities(eths);
    setAiClothingTypes(cloth);
    setAiBustFinishes(bustF);
    setAiBustColors(bustC);

    // Return defaults + catalog for dependency sync on load
    const catalog = { modelTypes: mts, outputFormats: ofs, mediaQualities: mqs };
    const restrictions = {
      category_ids:      cats.map(c => c.id),
      model_type_ids:    mts.map(m => m.id),
      output_format_ids: ofs.map(o => o.id),
      media_quality_ids: mqs.map(q => q.id),
      ethnicity_ids:     eths.map(e => e.id),
      clothing_type_ids: cloth.map(c => c.id),
      bust_finish_ids:   bustF.map(b => b.id),
      bust_color_ids:    bustC.map(b => b.id),
    };
    return { restrictions, catalog };
  };

  const restrictionCatalog = useMemo(() => ({
    modelTypes: aiModelTypes,
    outputFormats: aiOutputFormats,
    mediaQualities: aiMediaQualities,
  }), [aiModelTypes, aiOutputFormats, aiMediaQualities]);

  // Every restriction toggle runs through dependency rules (e.g. no bust type → no Bust Display)
  const updatePlanRestrictions = useCallback((updater) => {
    setPlanRestrictions((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      return applyRestrictionDependencies(next, restrictionCatalog);
    });
  }, [restrictionCatalog]);

  const updateAddonRestrictions = useCallback((updater) => {
    setAddonRestrictions((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      return applyRestrictionDependencies(next, restrictionCatalog);
    });
  }, [restrictionCatalog]);

  // ── debounced list search ────────────────────────────────────────────────

  const handleSearchInput = (val) => {
    setSearchInput(val);
    clearTimeout(searchDebounceTimer.current);
    searchDebounceTimer.current = setTimeout(() => setSearch(val.trim()), 350);
  };

  const hasActiveFilters = searchInput || filterType !== 'all' || filterStatus !== 'active' || filterVisibility !== 'all';

  const clearAllFilters = () => {
    setSearchInput('');
    setSearch('');
    setFilterType('all');
    setFilterStatus('active');
    setFilterVisibility('all');
  };

  // ── merchant search ────────────────────────────────────────────────────

  const handleMerchantSearch = (q) => {
    clearTimeout(merchantTimer.current);
    if (!q.trim()) { setMerchantSearchResults([]); return; }
    merchantTimer.current = setTimeout(async () => {
      setMerchantSearchLoading(true);
      try { const r = await api.searchMerchants(q); setMerchantSearchResults(r.data.items || []); }
      catch {}
      finally { setMerchantSearchLoading(false); }
    }, 350);
  };

  const addMerchant = (m) => {
    if (!m || allowedMerchants.some(x => x.user_id === m.user_id)) return;
    setAllowedMerchants(p => [...p, m]);
    setMerchantSearchResults([]);
    setSelectedMerchantIds(new Set());
  };

  // ── reset / open ───────────────────────────────────────────────────────

  const resetState = (asAddon) => {
    setAllowedMerchants([]); setMerchantSearchResults([]);
    setIsViewMode(false); setAllVersions([]); setSelectedViewVersionIdx(0);
    setVersionDiff({ pricingKeys: new Set(), rbacActionIds: new Set(), vsVersionNumber: null });
    setIsDirtyPricing(false); setIsDirtyRbac(false); setIsDirtyLabels(false); setFeatureLabels([]); setPricingErrors({}); setRbacErrors({}); setBackConfirm(false); setRemoveCurrencyConfirm(null); setDiffMode('previous');
    planCodeTouched.current = false;
    if (!asAddon) {
      setPlanForm({ plan_code: '', display_name: '', description: '',
        product_type: 'subscription', visibility: 'public',
        show_in_merchant_catalog: true, sales_model: 'self_serve', sort_order: 0, plan_tier: 0 });
      setPricingGrid({}); setPlanRbac({}); setPlanRbacLimits({});
      setPlanRestrictions({ ...EMPTY_RESTRICTIONS });
      setDraftVersion(null); setPublishedVersion(null);
    } else {
      setAddonForm({ addon_code: '', display_name: '', description: '',
        addon_category: 'token_pack', visibility: 'public', sort_order: 0 });
      setAddonPricing([]); setAddonRbac({}); setAddonRbacLimits({});
      setAddonEligiblePlanIds([]);
      setAddonRestrictions({ ...EMPTY_RESTRICTIONS });
    }
  };

  const handleExport = async () => {
    try {
      const res = await api.exportPlans({
        include_archived: filterStatus === 'archived' ? 'true' : 'false',
        search: search || undefined,
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `plans_addons_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { show('Export failed', 'error'); }
  };

  const openCreate = async (asAddon = false) => {
    setEditorLoading(true);
    try {
      const { restrictions: defaults, catalog } = await loadEditorData();
      if (asAddon && !allPlans.length) {
        try { const r = await api.getPlans({ per_page: 100 }); setAllPlans(r.data.items || []); } catch {}
      }
      resetState(asAddon);
      // Default: all options selected (no restrictions), with dependency sync
      if (!asAddon) setPlanRestrictions(applyRestrictionDependencies(defaults, catalog));
      else          setAddonRestrictions(applyRestrictionDependencies(defaults, catalog));
      setEditingId(null); setIsAddon(asAddon); setView('edit');
    } finally { setEditorLoading(false); }
  };

  // Load plan/add-on API payload into editor state (draft preferred after ensure-draft)
  const populatePlanEditor = (d, defaults, catalog) => {
    setPlanForm({ plan_code: d.plan_code, display_name: d.display_name,
      description: d.description || '', product_type: d.product_type,
      visibility: d.visibility, show_in_merchant_catalog: d.show_in_merchant_catalog,
      sales_model: d.sales_model, sort_order: d.sort_order, plan_tier: d.plan_tier ?? 0 });
    setAllowedMerchants(d.allowed_merchants || []);
    setPublishedVersion(d.published_version);
    setDraftVersion(d.draft_version);

    const pub = d.published_version;
    const drf = d.draft_version;
    // Prefer draft only when it is the working copy (version > published)
    const source = (drf && pub && drf.version_number > pub.version_number)
      ? drf
      : (drf && !pub) ? drf : (pub || drf);

    const grid = {};
    (source?.pricing || []).forEach(row => {
      if (!grid[row.currency_code]) grid[row.currency_code] = {};
      grid[row.currency_code][row.billing_period] = { ...row };
    });
    setPricingGrid(grid);
    const rbac = {}; const rbacLimits = {};
    (source?.rbac || []).forEach(r => {
      rbac[r.action_id] = r.is_granted;
      rbacLimits[r.action_id] = r.usage_limit ?? null;
    });
    setPlanRbac(rbac); setPlanRbacLimits(rbacLimits);
    setPlanRestrictions(applyRestrictionDependencies(
      mergeSavedRestrictions(d.content_restrictions, defaults), catalog,
    ));
  };

  const populateAddonEditor = (d, defaults, catalog) => {
    setAddonForm({ addon_code: d.addon_code, display_name: d.display_name,
      description: d.description || '', addon_category: d.addon_category,
      visibility: d.visibility, sort_order: d.sort_order });
    setAllowedMerchants(d.allowed_merchants || []);
    setAddonPricing(d.pricing || []);
    const rbac = {}; const rbacLimits = {};
    (d.rbac || []).forEach(r => {
      rbac[r.action_id] = r.is_granted;
      rbacLimits[r.action_id] = r.usage_limit ?? null;
    });
    setAddonRbac(rbac); setAddonRbacLimits(rbacLimits);
    setAddonEligiblePlanIds(d.eligible_plan_ids || []);
    setAddonRestrictions(applyRestrictionDependencies(
      mergeSavedRestrictions(d.content_restrictions, defaults), catalog,
    ));
  };

  const openPlanEdit = async (plan) => {
    setEditorLoading(true);
    try {
      const { restrictions: defaults, catalog } = await loadEditorData();
      // Load plan data without creating a draft — draft is created lazily at save time
      // only when pricing or RBAC has actually changed (G01 fix)
      const { data: d } = await api.getPlan(plan.id);
      populatePlanEditor(d, defaults, catalog);
      const labelRes = await api.getPlanFeatureLabels(plan.id).catch(() => null);
      setFeatureLabels((labelRes?.data?.labels || []).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)));
      setIsDirtyPricing(false); setIsDirtyRbac(false); setIsDirtyLabels(false);
      setEditingId(plan.id); setIsAddon(false); setView('edit');
    } catch { show('Failed to load plan', 'error'); }
    finally { setEditorLoading(false); }
  };

  const openAddonEdit = async (addon) => {
    setEditorLoading(true);
    try {
      const { restrictions: defaults, catalog } = await loadEditorData();
      if (!allPlans.length) {
        try { const r = await api.getPlans({ per_page: 100 }); setAllPlans(r.data.items || []); } catch {}
      }
      const { data: d } = await api.getAddon(addon.id);
      populateAddonEditor(d, defaults, catalog);
      const labelRes = await api.getAddonFeatureLabels(addon.id).catch(() => null);
      setFeatureLabels((labelRes?.data?.labels || []).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)));
      setIsDirtyPricing(false); setIsDirtyRbac(false); setIsDirtyLabels(false);
      setEditingId(addon.id); setIsAddon(true); setView('edit');
    } catch { show('Failed to load add-on', 'error'); }
    finally { setEditorLoading(false); }
  };

  const openPlanView = async (plan) => {
    setEditorLoading(true);
    try {
      const { restrictions: defaults, catalog } = await loadEditorData();
      const { data: d } = await api.getPlan(plan.id);
      const versions = d.all_versions || [];
      setAllVersions(versions);
      setSelectedViewVersionIdx(0);
      // Show the most recent version; diff it against the previous version
      const latest = versions[0];
      const previous = versions[1] || null;
      setVersionDiff(latest ? computeVersionDiff(latest, previous) : { pricingKeys: new Set(), rbacActionIds: new Set(), vsVersionNumber: null });
      const syntheticData = { ...d, draft_version: null, published_version: latest || d.published_version };
      populatePlanEditor(syntheticData, defaults, catalog);
      setIsViewMode(true);
      setEditingId(plan.id); setIsAddon(false); setView('edit');
    } catch { show('Failed to load plan', 'error'); }
    finally { setEditorLoading(false); }
  };

  const openAddonView = async (addon) => {
    setEditorLoading(true);
    try {
      const { restrictions: defaults, catalog } = await loadEditorData();
      if (!allPlans.length) {
        try { const r = await api.getPlans({ per_page: 100 }); setAllPlans(r.data.items || []); } catch {}
      }
      const { data: d } = await api.getAddon(addon.id);
      setAllVersions([]);
      setSelectedViewVersionIdx(0);
      populateAddonEditor(d, defaults, catalog);
      setIsViewMode(true);
      setEditingId(addon.id); setIsAddon(true); setView('edit');
    } catch { show('Failed to load add-on', 'error'); }
    finally { setEditorLoading(false); }
  };

  const computeVersionDiff = (current, previous) => {
    const pricingKeys = new Set();
    const rbacActionIds = new Set();
    if (!previous) return { pricingKeys, rbacActionIds, vsVersionNumber: null };

    const currP = {}; (current.pricing || []).forEach(r => { currP[`${r.currency_code}/${r.billing_period}`] = r; });
    const prevP = {}; (previous.pricing || []).forEach(r => { prevP[`${r.currency_code}/${r.billing_period}`] = r; });
    for (const key of new Set([...Object.keys(currP), ...Object.keys(prevP)])) {
      const c = currP[key]; const p = prevP[key];
      if (!c || !p ||
          String(c.mrp_excl_tax) !== String(p.mrp_excl_tax) ||
          String(c.discounted_price_excl_tax) !== String(p.discounted_price_excl_tax) ||
          String(c.bundled_tokens) !== String(p.bundled_tokens) ||
          String(c.yearly_bonus_pct) !== String(p.yearly_bonus_pct) ||
          !!c.is_period_active !== !!p.is_period_active) {
        pricingKeys.add(key);
      }
    }

    const currR = {}; (current.rbac || []).forEach(r => { currR[r.action_id] = r; });
    const prevR = {}; (previous.rbac || []).forEach(r => { prevR[r.action_id] = r; });
    for (const id of new Set([...Object.keys(currR), ...Object.keys(prevR)].map(Number))) {
      const c = currR[id]; const p = prevR[id];
      if (!c || !p || !!c.is_granted !== !!p.is_granted || c.usage_limit !== p.usage_limit) {
        rbacActionIds.add(id);
      }
    }

    return { pricingKeys, rbacActionIds, vsVersionNumber: previous.version_number };
  };

  const switchViewVersion = (idx) => {
    const v = allVersions[idx];
    if (!v) return;
    setSelectedViewVersionIdx(idx);
    const grid = {};
    (v.pricing || []).forEach(row => {
      if (!grid[row.currency_code]) grid[row.currency_code] = {};
      grid[row.currency_code][row.billing_period] = { ...row };
    });
    setPricingGrid(grid);
    const rbac = {}; const rbacLimits = {};
    (v.rbac || []).forEach(r => {
      rbac[r.action_id] = r.is_granted;
      rbacLimits[r.action_id] = r.usage_limit ?? null;
    });
    setPlanRbac(rbac); setPlanRbacLimits(rbacLimits);
    const publishedV = allVersions.find(av => av.version_status === 'published') || null;
    const previousV  = allVersions[idx + 1] || null;
    const compareAgainst = diffMode === 'published' ? publishedV : previousV;
    setVersionDiff(computeVersionDiff(v, compareAgainst));
  };

  const switchToEdit = async () => {
    if (isAddon) {
      setIsViewMode(false);
    } else {
      setEditorLoading(true);
      try {
        const { restrictions: defaults, catalog } = await loadEditorData();
        const { data: d } = await api.ensurePlanDraft(editingId);
        populatePlanEditor(d, defaults, catalog);
        setIsViewMode(false); setAllVersions([]);
        setIsDirtyPricing(false); setIsDirtyRbac(false);
      } catch { show('Failed to open editor', 'error'); }
      finally { setEditorLoading(false); }
    }
  };

  // ── validation (TC02 / TC03 / TC04) ──────────────────────────────────

  const planCodeError = (val) => {
    if (!val || !val.trim()) return 'Plan Code is required';
    if (!/^[a-z0-9_]+$/.test(val)) return 'Only a–z, 0–9 and underscore allowed';
    if (val.length > 100) return 'Max 100 characters';
    return '';
  };
  const planNameError = (val) => {
    if (!val || !val.trim()) return 'Display Name is required';
    if (val.trim().length > 200) return 'Max 200 characters';
    return '';
  };
  const addonCodeError = (val) => {
    if (!val || !val.trim()) return 'Add-on Code is required';
    if (!/^[a-z0-9_]+$/.test(val)) return 'Only a–z, 0–9 and underscore allowed';
    if (val.length > 100) return 'Max 100 characters';
    return '';
  };

  const validatePlan = () => {
    const errs = {};
    const ne = planNameError(planForm.display_name);
    if (ne) errs.display_name = ne;
    if (!editingId) {
      const ce = planCodeError(planForm.plan_code);
      if (ce) errs.plan_code = ce;
    }
    if (planForm.sort_order < 0) errs.sort_order = 'Sort order must be ≥ 0';
    if (planForm.plan_tier < 0 || planForm.plan_tier > 9) errs.plan_tier = 'Plan tier must be 0–9';
    setPlanErrors(errs);

    // G09: active periods must have a price; G07: bonus pct <= 100
    const pErrs = {};
    Object.entries(pricingGrid).forEach(([cc, periods]) =>
      Object.entries(periods).forEach(([bp, row]) => {
        if (row.is_period_active && (row.mrp_excl_tax === '' || row.mrp_excl_tax == null)) {
          pErrs[`${cc}/${bp}`] = 'Price required when period is active';
        }
        const bonus = parseFloat(row.yearly_bonus_pct);
        if (bp === 'yearly' && !isNaN(bonus) && bonus > 100) {
          pErrs[`${cc}/${bp}`] = (pErrs[`${cc}/${bp}`] ? pErrs[`${cc}/${bp}`] + '; ' : '') + 'Yearly bonus % must be ≤ 100';
        }
        const mrp  = parseFloat(row.mrp_excl_tax);
        const disc = parseFloat(row.discounted_price_excl_tax);
        if (!isNaN(mrp) && !isNaN(disc) && disc >= mrp) {
          pErrs[`${cc}/${bp}`] = (pErrs[`${cc}/${bp}`] ? pErrs[`${cc}/${bp}`] + '; ' : '') + 'Discounted price must be less than MRP';
        }
      })
    );
    setPricingErrors(pErrs);

    // G08: usage_limit >= 1 — per-action error map
    const rErrs = {};
    Object.entries(planRbacLimits).forEach(([aid, limit]) => {
      if (limit !== '' && limit !== null && parseInt(limit, 10) < 1)
        rErrs[aid] = 'Must be ≥ 1';
    });
    setRbacErrors(rErrs);

    return Object.keys(errs).length === 0 && Object.keys(pErrs).length === 0 && Object.keys(rErrs).length === 0;
  };

  const validateAddon = () => {
    const errs = {};
    const ne = planNameError(addonForm.display_name);
    if (ne) errs.display_name = ne;
    if (!editingId) {
      const ce = addonCodeError(addonForm.addon_code);
      if (ce) errs.addon_code = ce;
    }
    if (addonPricing.length === 0) errs._pricing = 'At least one pricing row is required';
    addonPricing.forEach((row, i) => {
      const mrp  = parseFloat(row.price);
      const disc = parseFloat(row.discounted_price_excl_tax);
      if (!isNaN(mrp) && !isNaN(disc) && disc >= mrp) {
        errs[`_pricing_${i}`] = 'Discounted price must be less than MRP';
      }
    });
    setAddonErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── save ───────────────────────────────────────────────────────────────

  const savePlan = async () => {
    if (!validatePlan()) { show('Please fix the errors before saving', 'error'); return; }
    setSaving(true);
    try {
      let id = editingId;
      const isNew = !id;
      const planPayload = {
        ...planForm,
        content_restrictions: applyRestrictionDependencies(planRestrictions, restrictionCatalog),
      };

      // Save basics directly (no draft needed for name/description/visibility changes)
      if (!id) { const r = await api.createPlan(planPayload); id = r.data.id; setEditingId(id); }
      else { await api.updatePlan(id, planPayload); }

      if (planForm.visibility === 'users')
        await api.updatePlanVisibility(id, { visibility: 'users', allowed_merchant_ids: allowedMerchants.map(m => m.user_id) });

      // Only create/update a draft version when pricing or RBAC was actually changed (G01)
      if (isNew || isDirtyPricing || isDirtyRbac) {
        await api.ensurePlanDraft(id);

        if (isNew || isDirtyPricing) {
          const rows = [];
          Object.entries(pricingGrid).forEach(([cc, periods]) =>
            Object.entries(periods).forEach(([bp, row]) => {
              if (row.mrp_excl_tax !== '' && row.mrp_excl_tax !== undefined) {
                const rule = taxRules.find(r => r.id === row.tax_rule_id);
                const rate = rule ? rule.effective_rate / 100 : 0;
                rows.push({
                  currency_code: cc, billing_period: bp, ...row,
                  mrp_incl_tax: rate && row.mrp_excl_tax
                    ? parseFloat((parseFloat(row.mrp_excl_tax) * (1 + rate)).toFixed(2)) : null,
                  discounted_price_incl_tax: rate && row.discounted_price_excl_tax
                    ? parseFloat((parseFloat(row.discounted_price_excl_tax) * (1 + rate)).toFixed(2)) : null,
                });
              }
            })
          );
          if (rows.length) await api.savePlanPricing(id, { pricing: rows });
        }

        if (isNew || isDirtyRbac) {
          const rbacRows = merchantModules.flatMap(m =>
            m.actions.map(a => {
              const lv = planRbacLimits[a.action_id];
              return {
                module_id: m.module_id, action_id: a.action_id,
                is_granted: !!planRbac[a.action_id],
                usage_limit: planRbac[a.action_id] && lv !== '' && lv != null ? parseInt(lv, 10) : null,
              };
            })
          );
          if (rbacRows.length) await api.savePlanRbac(id, { rbac: rbacRows });
        }
      }

      setIsDirtyPricing(false); setIsDirtyRbac(false);

      if (id && isDirtyLabels) {
        await api.savePlanFeatureLabels(id, { labels: featureLabels.map((f, i) => ({ ...f, sort_order: i })) });
        setIsDirtyLabels(false);
      }

      show(isNew ? 'Plan created successfully' : (isDirtyPricing || isDirtyRbac || isDirtyLabels ? 'Draft saved' : 'Plan saved'));
      const { restrictions: defaults, catalog } = await loadEditorData();
      const r2 = await api.getPlan(id);
      populatePlanEditor(r2.data, defaults, catalog);
      const labelRes2 = await api.getPlanFeatureLabels(id).catch(() => null);
      setFeatureLabels((labelRes2?.data?.labels || []).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)));
    } catch (e) { show(e.response?.data?.message || 'Save failed', 'error'); }
    finally { setSaving(false); }
  };

  const publishPlan = async () => {
    setPublishing(true);
    setPublishAnchor(null);
    try {
      // Auto-save any unsaved basics / pricing / RBAC before publishing
      const planPayload = {
        ...planForm,
        content_restrictions: applyRestrictionDependencies(planRestrictions, restrictionCatalog),
      };
      await api.updatePlan(editingId, planPayload);
      if (planForm.visibility === 'users')
        await api.updatePlanVisibility(editingId, { visibility: 'users', allowed_merchant_ids: allowedMerchants.map(m => m.user_id) });
      if (isDirtyPricing || isDirtyRbac) {
        await api.ensurePlanDraft(editingId);
        if (isDirtyPricing) {
          const rows = [];
          Object.entries(pricingGrid).forEach(([cc, periods]) =>
            Object.entries(periods).forEach(([bp, row]) => {
              if (row.mrp_excl_tax === '' || row.mrp_excl_tax == null) return;
              rows.push({ currency_code: cc, billing_period: bp, is_period_active: !!row.is_period_active,
                mrp_excl_tax: parseFloat(row.mrp_excl_tax),
                discounted_price_excl_tax: row.discounted_price_excl_tax ? parseFloat(row.discounted_price_excl_tax) : null,
                bundled_tokens: row.bundled_tokens || 0, yearly_bonus_pct: row.yearly_bonus_pct || 0,
                duration_days: bp === 'one_time' && row.duration_days ? parseInt(row.duration_days, 10) : null,
                tax_rule_id: row.tax_rule_id || null, sort_order: row.sort_order || 0 });
            })
          );
          if (rows.length) await api.savePlanPricing(editingId, { pricing: rows });
        }
        if (isDirtyRbac) {
          const rbacRows = merchantModules.flatMap(m =>
            m.actions.map(a => {
              const lv = planRbacLimits[a.action_id];
              return { module_id: m.module_id, action_id: a.action_id,
                is_granted: !!planRbac[a.action_id],
                usage_limit: planRbac[a.action_id] && lv !== '' && lv != null ? parseInt(lv, 10) : null };
            })
          );
          if (rbacRows.length) await api.savePlanRbac(editingId, { rbac: rbacRows });
        }
        setIsDirtyPricing(false); setIsDirtyRbac(false);
      }
      if (isDirtyLabels) {
        await api.savePlanFeatureLabels(editingId, { labels: featureLabels.map((f, i) => ({ ...f, sort_order: i })) });
        setIsDirtyLabels(false);
      }

      const r = await api.publishPlan(editingId, { publish_scope: publishScope, publish_notes: publishNotes });
      const migrated = r.data?.migrated_subscriptions ?? 0;
      show(
        publishScope === 'migrate_all' && migrated > 0
          ? `Plan published — ${migrated} active subscriber${migrated === 1 ? '' : 's'} migrated`
          : 'Plan published'
      );
      const refreshed = await api.getPlan(editingId);
      setPublishedVersion(refreshed.data.published_version); setDraftVersion(null);
      loadItems();
    } catch (e) { show(e.response?.data?.message || 'Publish failed', 'error'); }
    finally { setPublishing(false); }
  };

  const autoPopulateFeatureLabels = () => {
    const activeRbac = isAddon ? addonRbac : planRbac;
    const existingModuleIds = new Set(featureLabels.map(f => f.module_id).filter(Boolean));
    const newLabels = merchantModules
      .filter(m => m.module_tier !== 'core' && m.actions.some(a => activeRbac[a.action_id]))
      .filter(m => !existingModuleIds.has(m.module_id))
      .map(m => ({ module_id: m.module_id, label: m.module_name, sort_order: 0 }));
    if (!newLabels.length) { show('No new features to add', 'info'); return; }
    setFeatureLabels(prev => [
      ...prev,
      ...newLabels,
    ].map((f, i) => ({ ...f, sort_order: i })));
    setIsDirtyLabels(true);
  };

  const saveAddon = async () => {
    if (!validateAddon()) { show('Please fix the errors before saving', 'error'); return; }
    setSaving(true);
    try {
      const enrichedPricing = addonPricing.map(row => {
        const rule = taxRules.find(r => r.id === row.tax_rule_id);
        const rate = rule ? rule.effective_rate / 100 : 0;
        return {
          ...row,
          mrp_incl_tax: rate && row.price
            ? parseFloat((parseFloat(row.price) * (1 + rate)).toFixed(2)) : null,
          discounted_price_incl_tax: rate && row.discounted_price_excl_tax
            ? parseFloat((parseFloat(row.discounted_price_excl_tax) * (1 + rate)).toFixed(2)) : null,
        };
      });
      const payload = {
        ...addonForm,
        content_restrictions: applyRestrictionDependencies(addonRestrictions, restrictionCatalog),
        pricing: enrichedPricing,
        rbac: Object.entries(addonRbac).map(([aid, granted]) => {
          const mod = merchantModules.find(m => m.actions.some(a => a.action_id === +aid));
          return {
            module_id: mod?.module_id, action_id: +aid, is_granted: granted,
            usage_limit: granted ? (addonRbacLimits[+aid] ?? null) : null,
          };
        }).filter(r => r.module_id),
        eligible_plan_ids: addonEligiblePlanIds,
      };
      let id = editingId;
      const isNew = !id;
      if (id) { await api.updateAddon(id, payload); }
      else { const r = await api.createAddon(payload); id = r.data.id; setEditingId(id); }
      if (addonForm.visibility === 'users' && id)
        await api.updateAddonVisibility(id, { visibility: 'users', allowed_merchant_ids: allowedMerchants.map(m => m.user_id) });
      if (isDirtyLabels) {
        await api.saveAddonFeatureLabels(id, { labels: featureLabels.map((f, i) => ({ ...f, sort_order: i })) });
        setIsDirtyLabels(false);
      }
      if (isNew) { show('Add-on created successfully'); setView('list'); }
      else {
        show('Add-on saved');
        const { restrictions: defaults, catalog } = await loadEditorData();
        const r2 = await api.getAddon(id);
        populateAddonEditor(r2.data, defaults, catalog);
        const labelRes = await api.getAddonFeatureLabels(id).catch(() => null);
        setFeatureLabels((labelRes?.data?.labels || []).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)));
      }
    } catch (e) { show(e.response?.data?.message || 'Save failed', 'error'); }
    finally { setSaving(false); }
  };

  // ── pricing helpers ────────────────────────────────────────────────────

  const setCell = (currency, period, field, value) => {
    setIsDirtyPricing(true);
    setPricingGrid(prev => ({
      ...prev,
      [currency]: {
        ...(prev[currency] || {}),
        [period]: { ...(prev[currency]?.[period] || { is_period_active: false }), [field]: value },
      },
    }));
  };

  const toggleCurrency = (code) => {
    if (pricingGrid[code]) {
      const hasData = Object.values(pricingGrid[code]).some(row => row.mrp_excl_tax !== '' && row.mrp_excl_tax != null);
      if (hasData) { setRemoveCurrencyConfirm(code); return; }
      const next = { ...pricingGrid }; delete next[code]; setPricingGrid(next);
    } else {
      const blank = {};
      BILLING_PERIODS.forEach(bp => { blank[bp] = { is_period_active: false }; });
      setPricingGrid(prev => ({ ...prev, [code]: blank }));
    }
  };

  const nameToCode = (name) =>
    name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

  // ── LIST ───────────────────────────────────────────────────────────────

  const renderRow = (item, i) => {
    const isPlan = item._type === 'plan';
    const code   = isPlan ? item.plan_code : item.addon_code;
    const onEdit = () => isPlan ? openPlanEdit(item) : openAddonEdit(item);
    const onDup  = async () => {
      try {
        if (isPlan) { await api.duplicatePlan(item.id); show('Plan duplicated'); }
        else        { await api.duplicateAddon(item.id); show('Add-on duplicated'); }
        loadItems();
      } catch (e) { show(e.response?.data?.message || 'Failed', 'error'); }
    };
    const onArchive = () => setArchiveConfirm({ open: true, item });

    return (
      <TableRow key={`${item._type}-${item.id}`}
        sx={{
          '&:last-child td': { borderBottom: 0 },
          '&:hover': { bgcolor: 'primary.50' },
          transition: 'background 0.12s',
        }}>
        <TableCell sx={{ color: 'text.disabled', fontWeight: 500, pl: 2.5, width: 40 }}>{page * perPage + i + 1}</TableCell>
        <TableCell sx={{ maxWidth: 300 }}>
          <Tooltip title={item.display_name} placement="top" arrow>
            <Typography variant="body2" fontWeight={600}
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {item.display_name}
            </Typography>
          </Tooltip>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Tooltip title={code} placement="bottom" arrow>
              <Typography variant="caption" color="text.disabled"
                fontFamily="'Roboto Mono', monospace" fontSize={11}
                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180, display: 'block' }}>
                {code}
              </Typography>
            </Tooltip>
          </Box>
        </TableCell>
        <TableCell>
          {isPlan ? (
            <Chip label="Plan" size="small"
              sx={{ fontWeight: 700, fontSize: 11,
                bgcolor: 'primary.50', color: 'primary.dark',
                border: '1px solid', borderColor: 'primary.200' }} />
          ) : (
            <Chip label="Add-on" size="small"
              sx={{ fontWeight: 700, fontSize: 11,
                bgcolor: 'secondary.50', color: 'secondary.dark',
                border: '1px solid', borderColor: 'secondary.200' }} />
          )}
          <Typography variant="caption" color="text.disabled" display="block" mt={0.3}
            sx={{ textTransform: 'capitalize' }}>
            {isPlan
              ? item.product_type
              : (item.addon_category || '').replace(/_/g, ' ')}
          </Typography>
        </TableCell>
        <TableCell align="center">
          {isPlan ? (() => {
            const tier = item.plan_tier ?? 0;
            const TIER_LABELS = { 0: 'Free', 1: 'Entry', 2: 'Mid', 3: 'Pro', 4: 'Enterprise' };
            const TIER_COLORS = {
              0: { bg: '#f9fafb', border: '#e5e7eb', color: '#6b7280' },
              1: { bg: '#eff6ff', border: '#bfdbfe', color: '#1d4ed8' },
              2: { bg: '#f0fdf4', border: '#bbf7d0', color: '#15803d' },
              3: { bg: '#fdf4ff', border: '#e9d5ff', color: '#7e22ce' },
              4: { bg: '#fff7ed', border: '#fed7aa', color: '#c2410c' },
            };
            const c = TIER_COLORS[tier] || TIER_COLORS[4];
            const label = TIER_LABELS[tier] || `T${tier}`;
            return (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5,
                px: 1, py: 0.25, borderRadius: '20px',
                bgcolor: c.bg, border: `1px solid ${c.border}`, color: c.color,
                fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
                <Box component="span" sx={{ fontSize: 10, opacity: 0.7 }}>{tier}</Box>
                <Box component="span">{label}</Box>
              </Box>
            );
          })() : (
            <Typography sx={{ fontSize: 11, color: '#d1d5db' }}>—</Typography>
          )}
        </TableCell>
        <TableCell>
          <Box display="flex" gap={0.5} flexWrap="wrap" alignItems="center">
            <StatusBadge status={item.status} />
            {isPlan && item.published_version && (
              <Chip size="small" label={`v${item.published_version.version_number} ✓`}
                sx={{ height: 20, fontSize: 10, fontWeight: 700,
                  bgcolor: 'primary.50', color: 'primary.dark',
                  border: '1px solid', borderColor: 'primary.200' }} />
            )}
            {isPlan && item.draft_version && (
              <Chip size="small" label={`Draft v${item.draft_version.version_number}`}
                sx={{ height: 20, fontSize: 10, fontWeight: 700,
                  bgcolor: 'warning.50', color: 'warning.dark',
                  border: '1px solid', borderColor: 'warning.200' }} />
            )}
          </Box>
        </TableCell>
        <TableCell>
          <Chip size="small"
            label={item.visibility === 'public' ? 'Public' : 'Users only'}
            sx={{ fontSize: 11, fontWeight: 600,
              bgcolor: item.visibility === 'public' ? 'grey.100' : 'info.50',
              color: item.visibility === 'public' ? 'text.secondary' : 'info.dark',
              border: '1px solid',
              borderColor: item.visibility === 'public' ? 'grey.300' : 'info.200' }} />
        </TableCell>
        <TableCell align="right" sx={{ pr: 2 }}>
          <Box display="flex" gap={0.5} justifyContent="flex-end">
            {canViewDetail && (
              <Tooltip title="View details">
                <IconButton size="small"
                  onClick={() => isPlan ? openPlanView(item) : openAddonView(item)}
                  sx={BTN_GREY}>
                  <ViewIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
            {canUpdate && (
              <Tooltip title={`Edit ${isPlan ? 'plan' : 'add-on'}`}>
                <IconButton size="small" onClick={onEdit}
                  sx={BTN_PRIMARY}>
                  <Edit sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
            {canDuplicate && (
              <Tooltip title="Duplicate">
                <IconButton size="small" onClick={onDup} sx={BTN_GREY}>
                  <ContentCopy sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
            {canArchive && item.status === 1 && (
              <Tooltip title="Archive">
                <IconButton size="small" onClick={onArchive} sx={BTN_ERROR}>
                  <Archive sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  const renderList = () => (
    <Box>
      {/* Header — sticky */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start"
        mb={3} pb={2.5}
        sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: 'background.paper',
          borderBottom: '1px solid', borderColor: 'divider',
          mx: -3, px: 3, pt: 2.5,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
        <Box>
          <Typography variant="h5" fontWeight={800} letterSpacing={-0.5} gutterBottom>
            Plans &amp; Add-ons
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" color="text.secondary">
              Subscription plans and add-on products
            </Typography>
            {!listLoading && items.length > 0 && (
              <>
                <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'text.disabled' }} />
                <Typography variant="body2" color="text.disabled">
                  {items.filter(i => i._type === 'plan').length} plan{items.filter(i => i._type === 'plan').length !== 1 ? 's' : ''}
                  {' · '}
                  {items.filter(i => i._type === 'addon').length} add-on{items.filter(i => i._type === 'addon').length !== 1 ? 's' : ''}
                </Typography>
              </>
            )}
          </Box>
        </Box>
        <Box display="flex" gap={1.5} alignItems="center" mt={0.5}>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={loadItems}
              sx={{ border: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'grey.50' } }}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button size="medium" variant="outlined" startIcon={<FileDownloadOutlined />} onClick={handleExport}
            sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none' }}>
            Export CSV
          </Button>
          {canCreate && (
            <>
              <Button
                variant="contained" size="medium"
                startIcon={<Add />} endIcon={<KeyboardArrowDown />}
                onClick={e => setAddAnchor(e.currentTarget)}
                sx={{ borderRadius: 2, fontWeight: 700,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.2)' } }}>
                Add New
              </Button>
              <Menu
                anchorEl={addAnchor}
                open={Boolean(addAnchor)}
                onClose={() => setAddAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { mt: 0.5, borderRadius: 2, minWidth: 180,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)' } }}>
                <MenuItem onClick={() => { setAddAnchor(null); openCreate(false); }}
                  sx={{ py: 1.2 }}>
                  <ListItemIcon>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', mt: 0.2 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Subscription Plan"
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }} />
                </MenuItem>
                <MenuItem onClick={() => { setAddAnchor(null); openCreate(true); }}
                  sx={{ py: 1.2 }}>
                  <ListItemIcon>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'secondary.main', mt: 0.2 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Add-on"
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }} />
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Box>

      {/* Filters */}
      <Paper variant="outlined" sx={{ borderRadius: 2, mb: 2.5, overflow: 'hidden' }}>
        <Box display="flex" gap={1.5} px={2} py={1.5} alignItems="center" flexWrap="wrap"
          sx={{ bgcolor: 'grey.50' }}>
          <TuneRounded sx={{ fontSize: 18, color: 'text.disabled' }} />

          {/* TC01/TC05: debounced search — 350ms delay before API call */}
          <TextField size="small" placeholder="Search by name or code…" value={searchInput}
            onChange={e => handleSearchInput(e.target.value)}
            sx={{ width: 240, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 17, color: 'text.disabled' }} />
                </InputAdornment>
              ),
              endAdornment: listLoading && searchInput ? (
                <InputAdornment position="end">
                  <CircularProgress size={13} />
                </InputAdornment>
              ) : null,
            }} />

          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Type</InputLabel>
            <Select label="Type" value={filterType}
              onChange={e => setFilterType(e.target.value)}
              sx={{ borderRadius: 2, bgcolor: 'white' }}>
              <MenuItem value="all">All types</MenuItem>
              <MenuItem value="plan">Plans only</MenuItem>
              <MenuItem value="addon">Add-ons only</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              sx={{ borderRadius: 2, bgcolor: 'white' }}>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </Select>
          </FormControl>

          {/* TC03/TC06: labels aligned — "Users only (Hidden)" maps QA's "Hidden" */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Visibility</InputLabel>
            <Select label="Visibility" value={filterVisibility}
              onChange={e => setFilterVisibility(e.target.value)}
              sx={{ borderRadius: 2, bgcolor: 'white' }}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="public">Public (Visible)</MenuItem>
              <MenuItem value="users">Users only (Hidden)</MenuItem>
            </Select>
          </FormControl>

          {/* TC07: clear all filters */}
          {hasActiveFilters && (
            <Button size="small" onClick={clearAllFilters}
              sx={{ ml: 0.5, color: 'error.main', fontWeight: 600, fontSize: 12,
                '&:hover': { bgcolor: 'error.50' } }}>
              Clear filters
            </Button>
          )}

          <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
            {listLoading ? 'Loading…' : `${total} item${total !== 1 ? 's' : ''}`}
          </Typography>
        </Box>
      </Paper>

      {/* Table */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...TH, pl: 2.5, width: 40 }}>#</TableCell>
                <TableCell sx={TH}>
                  <SortTH label="Name" field="display_name" sortBy={sortBy} sortDir={sortDir}
                    onSort={col => { setSortBy(col); setSortDir(d => col === sortBy ? (d==='asc'?'desc':'asc') : 'asc'); }} />
                </TableCell>
                <TableCell sx={{ ...TH, width: 130 }}>Type</TableCell>
                <TableCell sx={{ ...TH, width: 70, textAlign: 'center' }}>Tier</TableCell>
                <TableCell sx={TH}>Status</TableCell>
                <TableCell sx={{ ...TH, width: 120 }}>Visibility</TableCell>
                <TableCell sx={{ ...TH, textAlign: 'right', pr: 2, width: 130 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {listLoading
                ? Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ pl: 2.5 }}><Skeleton width={16} /></TableCell>
                    <TableCell>
                      <Skeleton width={160} height={20} />
                      <Skeleton width={100} height={14} sx={{ mt: 0.5 }} />
                    </TableCell>
                    <TableCell><Skeleton variant="rounded" width={60} height={22} /></TableCell>
                    <TableCell align="center"><Skeleton variant="rounded" width={32} height={22} /></TableCell>
                    <TableCell><Skeleton variant="rounded" width={70} height={22} /></TableCell>
                    <TableCell><Skeleton variant="rounded" width={80} height={22} /></TableCell>
                    <TableCell align="right" sx={{ pr: 2 }}>
                      <Box display="flex" gap={0.5} justifyContent="flex-end">
                        <Skeleton variant="circular" width={28} height={28} />
                        <Skeleton variant="circular" width={28} height={28} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
                : items.length === 0
                  ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ border: 0 }}>
                        {search || filterType !== 'all' || filterStatus !== 'active' || filterVisibility !== 'all'
                          ? <EmptyState message="No results" sub="Try adjusting your search or filters" />
                          : <EmptyState message="Nothing here yet" sub="Create a plan or add-on to get started" />}
                      </TableCell>
                    </TableRow>
                  )
                  : (filterType === 'all'
                      ? items.slice(page * perPage, (page + 1) * perPage)
                      : items
                    ).map(renderRow)
              }
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={perPage}
        onRowsPerPageChange={e => { setPerPage(+e.target.value); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50]}
        sx={{ borderTop: '1px solid', borderColor: 'divider', mt: 0.5 }}
      />
    </Box>
  );

  // ── ALLOWLIST PANEL ────────────────────────────────────────────────────

  const handleExcelUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const text = evt.target.result;
        const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
        const dataLines = lines[0]?.toLowerCase().includes('name') ? lines.slice(1) : lines;

        // Parse rows — email (col 1) and mobile/whatsapp (col 2)
        const rowsFromFile = [];
        dataLines.forEach(line => {
          const cols = line.split(/[,\t]/).map(c => c.trim().replace(/^"|"$/g, ''));
          const email  = (cols[1] || '').toLowerCase().trim();
          const mobile = (cols[2] || '').trim();
          if (email || mobile) rowsFromFile.push({ email, mobile });
        });

        if (!rowsFromFile.length) {
          show('No data rows found in file', 'warning');
          return;
        }

        // Validate against real users (email first, whatsapp fallback for misses)
        const r = await api.bulkLookupMerchants(rowsFromFile);
        const { found = [], not_found = [] } = r.data;

        const newMerchants = found.filter(m => !allowedMerchants.some(x => x.user_id === m.user_id));
        if (newMerchants.length) setAllowedMerchants(p => [...p, ...newMerchants]);

        if (not_found.length === 0) {
          show(`Added ${newMerchants.length} merchant${newMerchants.length !== 1 ? 's' : ''} from file`);
        } else {
          const skipped = not_found.length;
          const added   = newMerchants.length;
          show(
            `Added ${added} merchant${added !== 1 ? 's' : ''}. ${skipped} email${skipped !== 1 ? 's' : ''} not found: ${not_found.slice(0, 3).join(', ')}${skipped > 3 ? ` +${skipped - 3} more` : ''}`,
            added > 0 ? 'warning' : 'error',
          );
        }
      } catch {
        show('Could not parse file. Use CSV with columns: Name, Email, Mobile', 'error');
      }
    };
    reader.readAsText(file);
  };

  const renderAllowlistPanel = () => (
    <Box mt={2.5} p={2} sx={{
      border: '1px dashed', borderColor: 'primary.300',
      borderRadius: 2, bgcolor: 'primary.50',
    }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
        <Box>
          <Typography variant="body2" fontWeight={700} color="primary.dark">
            Merchant Allowlist
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Only these merchants will see this plan in their catalog
          </Typography>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          {allowedMerchants.length > 0 && (
            <Chip label={`${allowedMerchants.length} added`} size="small" color="primary"
              sx={{ fontWeight: 700, height: 22 }} />
          )}
          <Tooltip title="Download a sample CSV template">
            <Button size="small" variant="text"
              sx={{ fontSize: 11, px: 1, py: 0.5, color: 'text.secondary',
                '&:hover': { color: 'primary.dark', bgcolor: 'primary.50' } }}
              onClick={() => {
                const csv = 'Name,Email,Mobile\nJohn Smith,john@example.com,9876543210\nPriya Sharma,priya@example.com,9123456789';
                const blob = new Blob([csv], { type: 'text/csv' });
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = 'allowlist_template.csv';
                a.click();
                URL.revokeObjectURL(a.href);
              }}>
              Sample
            </Button>
          </Tooltip>
          <Tooltip title="Upload CSV/Excel — columns: Name, Email, Mobile">
            <Button size="small" variant="outlined" component="label"
              sx={{ fontSize: 11, px: 1.5, py: 0.5, borderRadius: 1.5,
                borderColor: 'primary.300', color: 'primary.dark',
                '&:hover': { bgcolor: 'primary.100' } }}>
              Upload Excel
              <input ref={excelInputRef} type="file" hidden
                accept=".csv,.xlsx,.xls,.txt"
                onChange={handleExcelUpload} />
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* Search autocomplete */}
      <Autocomplete
        size="small"
        options={merchantSearchResults}
        getOptionLabel={o => o.display_name || ''}
        filterOptions={x => x}
        loading={merchantSearchLoading}
        noOptionsText={merchantSearchLoading ? 'Searching…' : 'No merchants found'}
        isOptionEqualToValue={(opt, val) => opt.user_id === val.user_id}
        onInputChange={(_, val, reason) => {
          if (reason === 'input') handleMerchantSearch(val);
          if (reason === 'clear') setMerchantSearchResults([]);
        }}
        onChange={(_, val) => {
          if (val) { addMerchant(val); }
        }}
        value={null}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.user_id}>
            <Box>
              <Typography variant="body2" fontWeight={600}>{option.display_name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {[option.email, option.mobile].filter(Boolean).join(' · ')}
              </Typography>
            </Box>
          </Box>
        )}
        renderInput={params => (
          <TextField {...params} placeholder="Search by name, email or WhatsApp number…"
            sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 17, color: 'text.disabled' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {merchantSearchLoading && <CircularProgress color="inherit" size={14} sx={{ mr: 1 }} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }} />
        )}
        sx={{ mb: allowedMerchants.length > 0 ? 2 : 0.5 }}
      />

      {allowedMerchants.length === 0 ? (
        <Typography variant="caption" color="primary.400">
          No merchants added yet. Search above or upload a CSV/Excel file.
        </Typography>
      ) : (() => {
        const allSelected = allowedMerchants.length > 0 &&
          allowedMerchants.every(m => selectedMerchantIds.has(m.user_id));
        const someSelected = !allSelected &&
          allowedMerchants.some(m => selectedMerchantIds.has(m.user_id));

        const toggleAll = () => {
          if (allSelected) {
            setSelectedMerchantIds(new Set());
          } else {
            setSelectedMerchantIds(new Set(allowedMerchants.map(m => m.user_id)));
          }
        };

        const toggleOne = (uid) => {
          setSelectedMerchantIds(prev => {
            const next = new Set(prev);
            next.has(uid) ? next.delete(uid) : next.add(uid);
            return next;
          });
        };

        const removeSelected = () => {
          setAllowedMerchants(p => p.filter(m => !selectedMerchantIds.has(m.user_id)));
          setSelectedMerchantIds(new Set());
        };

        return (
          <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden', bgcolor: 'white' }}>
            {/* Bulk action bar */}
            {selectedMerchantIds.size > 0 && (
              <Box display="flex" alignItems="center" justifyContent="space-between"
                px={2} py={1}
                sx={{ bgcolor: 'primary.50', borderBottom: '1px solid', borderColor: 'primary.200' }}>
                <Typography variant="body2" fontWeight={600} color="primary.dark">
                  {selectedMerchantIds.size} selected
                </Typography>
                <Button size="small" variant="contained" color="error"
                  startIcon={<Delete sx={{ fontSize: 15 }} />}
                  onClick={removeSelected}
                  sx={{ borderRadius: 1.5, fontWeight: 700, py: 0.4, fontSize: 12 }}>
                  Remove Selected
                </Button>
              </Box>
            )}

            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell padding="checkbox" sx={{ pl: 1.5 }}>
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={toggleAll}
                      sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
                    />
                  </TableCell>
                  <TableCell sx={{ ...TH }}>Name</TableCell>
                  <TableCell sx={TH}>Email</TableCell>
                  <TableCell sx={TH}>Mobile / WhatsApp</TableCell>
                  <TableCell sx={{ ...TH, width: 48 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {allowedMerchants.map(m => {
                  const isSelected = selectedMerchantIds.has(m.user_id);
                  return (
                    <TableRow key={m.user_id}
                      selected={isSelected}
                      onClick={() => toggleOne(m.user_id)}
                      sx={{
                        cursor: 'pointer',
                        '&:last-child td': { borderBottom: 0 },
                        bgcolor: isSelected ? 'primary.50' : 'transparent',
                        '&:hover': { bgcolor: isSelected ? 'primary.100' : 'grey.50' },
                        transition: 'background 0.1s',
                      }}>
                      <TableCell padding="checkbox" sx={{ pl: 1.5 }}>
                        <Checkbox
                          size="small"
                          checked={isSelected}
                          onChange={() => toggleOne(m.user_id)}
                          onClick={e => e.stopPropagation()}
                          sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{m.display_name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{m.email || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{m.mobile || '—'}</Typography>
                      </TableCell>
                      <TableCell onClick={e => e.stopPropagation()}>
                        <Tooltip title="Remove">
                          <IconButton size="small"
                            onClick={() => {
                              setAllowedMerchants(p => p.filter(x => x.user_id !== m.user_id));
                              setSelectedMerchantIds(prev => {
                                const next = new Set(prev); next.delete(m.user_id); return next;
                              });
                            }}
                            sx={{ color: 'error.main', '&:hover': { bgcolor: 'error.50' } }}>
                            <Delete sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        );
      })()}
    </Box>
  );

  // ── AI GENERATION RESTRICTIONS SECTION ───────────────────────────────────

  // Renders one restriction group using DB records (id-based).
  // For media_qualities the items carry applies_to ('IMAGE'|'VIDEO') so we group them.
  const renderRestrictionGroup = (title, dbOptions, selectedIds, fieldKey, setRestrictions, allRestrictions = null) => {
    if (!dbOptions.length) return null;

    const isModelTypeOptionDisabled = (option) => {
      if (fieldKey !== 'model_type_ids' || !allRestrictions) return false;
      const hasAny = (ids) => Array.isArray(ids) && ids.length > 0;
      if (_isBustDisplayModel(option)) {
        return !hasAny(allRestrictions.bust_finish_ids) || !hasAny(allRestrictions.bust_color_ids);
      }
      if (_isHumanModel(option) || _isHumanHandModel(option)) {
        return !hasAny(allRestrictions.ethnicity_ids) && !hasAny(allRestrictions.clothing_type_ids);
      }
      if (_isProductOnlyModel(option)) {
        const imageIds = aiOutputFormats
          .filter((of) => (of.name || '').toUpperCase() === 'IMAGE')
          .map((of) => of.id);
        return imageIds.length > 0 && !imageIds.some((id) => allRestrictions.output_format_ids.includes(id));
      }
      return false;
    };

    const disabledReason = (option) => {
      if (!isModelTypeOptionDisabled(option)) return '';
      const hasAny = (ids) => Array.isArray(ids) && ids.length > 0;
      if (_isBustDisplayModel(option)) return 'Requires at least one Bust Finish and one Bust Color';
      if (_isHumanModel(option) || _isHumanHandModel(option))
        return 'Requires at least one Ethnicity or Clothing Type';
      if (_isProductOnlyModel(option)) return 'Requires Image Output Format to be selected';
      return '';
    };

    const allOn  = dbOptions.every(o => selectedIds.includes(o.id));
    const someOn = !allOn && dbOptions.some(o => selectedIds.includes(o.id));

    const toggleId = (id) => setRestrictions(r => ({
      ...r,
      [fieldKey]: r[fieldKey].includes(id)
        ? r[fieldKey].filter(x => x !== id)
        : [...r[fieldKey], id],
    }));

    // Group by applies_to if the items have that field (media_qualities)
    const hasGroups = dbOptions.some(o => o.applies_to);
    const groups = hasGroups
      ? ['IMAGE', 'VIDEO'].map(type => ({
          type,
          items: dbOptions.filter(o => (o.applies_to || 'IMAGE').toUpperCase() === type),
        })).filter(g => g.items.length > 0)
      : null;

    const chipStyle = (on) => ({
      fontWeight: on ? 700 : 400, fontSize: 12,
      opacity: on ? 1 : 0.55, transition: 'all 0.15s',
    });

    return (
      <Box mb={2.5}>
        {/* Header with select-all */}
        <Box display="flex" alignItems="center" gap={0.5} mb={1.5}>
          <Checkbox size="small" checked={allOn} indeterminate={someOn}
            onChange={() => setRestrictions(r => ({
              ...r, [fieldKey]: allOn ? [] : dbOptions.map(o => o.id),
            }))}
            sx={{ '& .MuiSvgIcon-root': { fontSize: 20 }, p: 0.3 }} />
          <Typography variant="body2" fontWeight={700}>{title}</Typography>
          <Typography variant="caption" color="text.disabled" ml={0.5}>
            ({selectedIds.length}/{dbOptions.length})
          </Typography>
        </Box>

        {groups ? (
          /* Grouped by IMAGE / VIDEO */
          <Box display="flex" flexDirection="column" gap={1} pl={0.5}>
            {groups.map(({ type, items }) => {
              const groupIds = items.map(o => o.id);
              const groupAllOn = groupIds.every(id => selectedIds.includes(id));
              const groupSome  = !groupAllOn && groupIds.some(id => selectedIds.includes(id));
              return (
                <Box key={type}>
                  <Box display="flex" alignItems="center" gap={1} mb={0.8}>
                    <Checkbox size="small" checked={groupAllOn} indeterminate={groupSome}
                      onChange={() => setRestrictions(r => {
                        const deselect = groupIds.every(id => r[fieldKey].includes(id));
                        return {
                          ...r,
                          [fieldKey]: deselect
                            ? r[fieldKey].filter(id => !groupIds.includes(id))
                            : [...new Set([...r[fieldKey], ...groupIds])],
                        };
                      })}
                      sx={{ '& .MuiSvgIcon-root': { fontSize: 16 }, p: 0.2 }} />
                    <Chip label={type === 'IMAGE' ? '🖼 Image' : '🎬 Video'} size="small"
                      sx={{
                        fontSize: 11, fontWeight: 700, height: 20,
                        bgcolor: type === 'IMAGE' ? 'blue.50' : 'purple.50',
                        color: type === 'IMAGE' ? 'primary.dark' : 'secondary.dark',
                        border: '1px solid',
                        borderColor: type === 'IMAGE' ? 'primary.200' : 'secondary.200',
                        '& .MuiChip-label': { px: 1 },
                      }} />
                  </Box>
                  <Box display="flex" flexWrap="wrap" gap={0.8} pl={3.5}>
                    {items.map(o => {
                      const on = selectedIds.includes(o.id);
                      return (
                        <Chip key={o.id} label={o.display_name || o.name} size="small" clickable
                          color={on ? 'primary' : 'default'}
                          variant={on ? 'filled' : 'outlined'}
                          onClick={() => toggleId(o.id)}
                          sx={chipStyle(on)} />
                      );
                    })}
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : (
          /* Flat list (categories, model types, output formats) */
          <Box display="flex" flexWrap="wrap" gap={1} pl={0.5}>
            {dbOptions.map(o => {
              const on = selectedIds.includes(o.id);
              const disabled = isModelTypeOptionDisabled(o);
              const reason = disabledReason(o);
              const chip = (
                <Chip key={o.id} label={o.display_name || o.name} size="small" clickable={!disabled}
                  color={on ? 'primary' : 'default'}
                  variant={on ? 'filled' : 'outlined'}
                  onClick={() => { if (!disabled) toggleId(o.id); }}
                  sx={{ ...chipStyle(on), ...(disabled ? { opacity: 0.35, pointerEvents: 'none' } : {}) }} />
              );
              return reason
                ? <Tooltip key={o.id} title={reason} arrow><span>{chip}</span></Tooltip>
                : chip;
            })}
          </Box>
        )}
      </Box>
    );
  };

  // Plan-based generation allowlists — one row per master-data dimension
  const generationRestrictionGroups = [
    { title: 'Jewellery Categories', options: aiCategories,     fieldKey: 'category_ids' },
    { title: 'Model Types',          options: aiModelTypes,     fieldKey: 'model_type_ids' },
    { title: 'Output Format',        options: aiOutputFormats,  fieldKey: 'output_format_ids' },
    { title: 'Media Quality',        options: aiMediaQualities, fieldKey: 'media_quality_ids' },
    { title: 'Ethnicity',            options: aiEthnicities,    fieldKey: 'ethnicity_ids' },
    { title: 'Clothing Type',        options: aiClothingTypes,  fieldKey: 'clothing_type_ids' },
    { title: 'Bust Type',            options: aiBustFinishes,   fieldKey: 'bust_finish_ids' },
    { title: 'Bust Colors',          options: aiBustColors,     fieldKey: 'bust_color_ids' },
  ];

  const isGenerationRestricted = (restrictions) =>
    generationRestrictionGroups.some(
      ({ options, fieldKey }) => options.length && restrictions[fieldKey].length < options.length,
    );

  const renderAllGenerationRestrictions = (restrictions, setRestrictions) =>
    generationRestrictionGroups.map(({ title, options, fieldKey }) =>
      renderRestrictionGroup(title, options, restrictions[fieldKey], fieldKey, setRestrictions, restrictions),
    );

  const renderAiRestrictionsAccordion = (restrictions, setRestrictions) => {
    const isRestricted = isGenerationRestricted(restrictions);
    return (
      <Accordion disableGutters elevation={0} sx={ACCORDION_SX}>
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 1.5, minHeight: 52 }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <SectionHeader label="AI Generation Restrictions" />
            {isRestricted && (
              <Chip label="Restricted" size="small"
                sx={{ height: 18, fontSize: 10, fontWeight: 700,
                  bgcolor: 'warning.50', color: 'warning.dark',
                  border: '1px solid', borderColor: 'warning.200',
                  '& .MuiChip-label': { px: 1 } }} />
            )}
          </Box>
        </AccordionSummary>
        <Divider />
        <AccordionDetails sx={{ p: 3 }}>
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2, border: 'none', bgcolor: 'info.50' }}>
            Control which AI generation options merchants on this plan can access.
            Unchecked items will be hidden on the merchant Create page.
          </Alert>
          <Grid container spacing={3}>
            {(() => {
              const groups = renderAllGenerationRestrictions(restrictions, setRestrictions);
              return (
                <>
                  <Grid item xs={12} md={6}>{groups.slice(0, 4)}</Grid>
                  <Grid item xs={12} md={6}>{groups.slice(4)}</Grid>
                </>
              );
            })()}
          </Grid>
        </AccordionDetails>
      </Accordion>
    );
  };

  // ── PLAN EDITOR SECTIONS ───────────────────────────────────────────────

  const renderPlanSections = () => (
    <Box display="flex" flexDirection="column" gap={2}>

      <Accordion defaultExpanded disableGutters elevation={0} sx={ACCORDION_SX}>
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 1.5, minHeight: 52 }}>
          <SectionHeader label="Basics" />
        </AccordionSummary>
        <Divider />
        <AccordionDetails sx={{ p: 3 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Plan Code" value={planForm.plan_code}
                disabled={!!editingId}
                error={!!planErrors.plan_code}
                helperText={planErrors.plan_code || (editingId ? 'Locked after first save' : 'Auto-filled · a–z, 0–9, underscore')}
                onChange={e => {
                  const cleaned = e.target.value.toLowerCase();
                  planCodeTouched.current = true;
                  setPlanForm(f => ({ ...f, plan_code: cleaned }));
                  setPlanErrors(p => ({ ...p, plan_code: planCodeError(cleaned) || undefined }));
                }}
                onBlur={() => setPlanErrors(p => ({ ...p, plan_code: planCodeError(planForm.plan_code) || undefined }))}
                inputProps={{ maxLength: 100 }}
                sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: 13 } }} />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField fullWidth size="small" label="Display Name *" value={planForm.display_name}
                error={!!planErrors.display_name}
                helperText={planErrors.display_name || `${planForm.display_name.length}/200`}
                onChange={e => {
                  const name = e.target.value;
                  setPlanForm(f => ({ ...f, display_name: name,
                    ...(!editingId && !planCodeTouched.current && { plan_code: nameToCode(name) }) }));
                  setPlanErrors(p => ({ ...p, display_name: planNameError(name) || undefined }));
                }}
                onBlur={() => setPlanErrors(p => ({ ...p, display_name: planNameError(planForm.display_name) || undefined }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Description" multiline rows={2}
                value={planForm.description}
                onChange={e => setPlanForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Optional — shown to merchants in the plan catalog" />
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Visibility</InputLabel>
                <Select label="Visibility" value={planForm.visibility}
                  onChange={e => setPlanForm(f => ({ ...f, visibility: e.target.value }))}>
                  <MenuItem value="public">Public</MenuItem>
                  <MenuItem value="users">Users only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Sales Model</InputLabel>
                <Select label="Sales Model" value={planForm.sales_model}
                  onChange={e => setPlanForm(f => ({ ...f, sales_model: e.target.value }))}>
                  <MenuItem value="self_serve">Self-serve</MenuItem>
                  <MenuItem value="quote_only">Quote only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4} md={2}>
              <TextField fullWidth size="small" label="Sort Order" type="number"
                helperText={planErrors.sort_order || 'Card display order'}
                error={!!planErrors.sort_order}
                value={planForm.sort_order}
                inputProps={{ min: 0 }}
                onChange={e => { setPlanForm(f => ({ ...f, sort_order: +e.target.value })); setPlanErrors(p => { const n={...p}; delete n.sort_order; return n; }); }} />
            </Grid>
            <Grid item xs={4} md={2}>
              <TextField fullWidth size="small" label="Plan Tier" type="number"
                helperText={planErrors.plan_tier || 'Higher number = higher tier. Used for upgrade/downgrade logic. E.g. 0=Free, 1=Entry, 2=Mid, 3=Pro, 4=Enterprise. Add any value for future plans.'}
                error={!!planErrors.plan_tier}
                value={planForm.plan_tier}
                inputProps={{ min: 0, max: 9 }}
                onChange={e => { setPlanForm(f => ({ ...f, plan_tier: +e.target.value })); setPlanErrors(p => { const n={...p}; delete n.plan_tier; return n; }); }} />
            </Grid>
            <Grid item xs={8} md={4} display="flex" alignItems="center">
              <FormControlLabel
                control={<Checkbox sx={CB_SX} checked={planForm.show_in_merchant_catalog}
                  onChange={e => setPlanForm(f => ({ ...f, show_in_merchant_catalog: e.target.checked }))} />}
                label={<Typography variant="body2">Show in merchant catalog</Typography>} />
            </Grid>
          </Grid>
          {planForm.visibility === 'users' && renderAllowlistPanel()}
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded disableGutters elevation={0} sx={ACCORDION_SX}>
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 1.5, minHeight: 52 }}>
          <SectionHeader label="Pricing" />
        </AccordionSummary>
        <Divider />
        <AccordionDetails sx={{ p: 3 }}>
          <Box mb={2}>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              Select currencies to configure pricing
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              {currencies.length === 0
                ? <Typography variant="body2" color="text.disabled">No currencies found. Add them in Master Data first.</Typography>
                : currencies.map(c => (
                  <Chip key={c.code} label={`${c.symbol} ${c.code}`} size="small" clickable
                    color={pricingGrid[c.code] ? 'primary' : 'default'}
                    variant={pricingGrid[c.code] ? 'filled' : 'outlined'}
                    onClick={() => toggleCurrency(c.code)}
                    sx={{ fontWeight: 600, fontSize: 12 }} />
                ))}
            </Box>
          </Box>
          {Object.keys(pricingGrid).length === 0
            ? <Alert severity="info" sx={{ borderRadius: 2, border: 'none', bgcolor: 'info.50' }}>
                Select a currency above to configure pricing tiers.
              </Alert>
            : Object.entries(pricingGrid).map(([currency, periods]) => (
              <Box key={currency} mb={3}>
                <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
                  <Chip label={currency} size="small" color="primary" sx={{ fontWeight: 700 }} />
                  <Typography variant="body2" color="text.secondary">
                    {currencies.find(c => c.code === currency)?.display_name}
                  </Typography>
                </Box>
                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 720 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...TH, pl: 2, width: 72 }}>Active</TableCell>
                        <TableCell sx={{ ...TH, width: 90 }}>Period</TableCell>
                        <TableCell sx={{ ...TH, width: 130 }}>MRP (excl. tax)</TableCell>
                        <TableCell sx={{ ...TH, width: 130 }}>Disc. price (excl.)</TableCell>
                        <TableCell sx={{ ...TH, width: 185 }}>Tax rule</TableCell>
                        <TableCell sx={{ ...TH, width: 120 }}>Incl. tax</TableCell>
                        <TableCell sx={{ ...TH, width: 100 }}>Tokens</TableCell>
                        <TableCell sx={{ ...TH, width: 110 }}>Duration (days)</TableCell>
                        <TableCell sx={{ ...TH, width: 110 }}>Yearly bonus</TableCell>
                        <TableCell sx={{ ...TH, width: 100 }}>Auto-renew</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {BILLING_PERIODS.map(bp => {
                        const cell = periods[bp] || {};
                        const rule = taxRules.find(r => r.id === cell.tax_rule_id);
                        const incl = cell.mrp_excl_tax && rule
                          ? (parseFloat(cell.mrp_excl_tax) * (1 + rule.effective_rate / 100)).toFixed(2)
                          : null;
                        const isDiffRow = isViewMode && versionDiff.pricingKeys.has(`${currency}/${bp}`);
                        const priceErr = pricingErrors[`${currency}/${bp}`];
                        return (
                          <TableRow key={bp}
                            sx={{ opacity: cell.is_period_active ? 1 : 0.38, transition: 'opacity 0.15s',
                              bgcolor: priceErr ? 'error.50' : (isDiffRow ? 'warning.50' : (cell.is_period_active ? 'transparent' : 'grey.50')),
                              outline: isDiffRow ? '2px solid' : 'none',
                              outlineColor: isDiffRow ? 'warning.300' : 'transparent',
                              outlineOffset: '-1px',
                              '&:last-child td': { borderBottom: 0 } }}>
                            <TableCell sx={{ pl: 1.5 }}>
                              <Switch size="small" checked={!!cell.is_period_active}
                                onChange={e => setCell(currency, bp, 'is_period_active', e.target.checked)} />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>{PERIOD_LABELS[bp]}</Typography>
                            </TableCell>
                            <TableCell>
                              <TextField size="small" type="number" placeholder="0.00"
                                value={cell.mrp_excl_tax ?? ''}
                                error={!!priceErr}
                                helperText={priceErr || undefined}
                                onChange={e => { setCell(currency, bp, 'mrp_excl_tax', e.target.value); setPricingErrors(p => { const n={...p}; delete n[`${currency}/${bp}`]; return n; }); }}
                                sx={{ width: 110 }} InputProps={{ inputProps: { min: 0, step: '0.01' } }} />
                            </TableCell>
                            <TableCell>
                              <TextField size="small" type="number" placeholder="optional"
                                value={cell.discounted_price_excl_tax ?? ''}
                                onChange={e => { setCell(currency, bp, 'discounted_price_excl_tax', e.target.value || null); setPricingErrors(p => { const n={...p}; delete n[`${currency}/${bp}`]; return n; }); }}
                                sx={{ width: 110 }} InputProps={{ inputProps: { min: 0, step: '0.01' } }} />
                            </TableCell>
                            <TableCell>
                              <FormControl size="small" sx={{ width: 168 }}>
                                <Select displayEmpty value={cell.tax_rule_id || ''}
                                  onChange={e => setCell(currency, bp, 'tax_rule_id', e.target.value || null)}>
                                  <MenuItem value=""><em style={{ color: '#999' }}>No tax</em></MenuItem>
                                  {taxRules.filter(r => r.currency_code === currency).map(r => (
                                    <MenuItem key={r.id} value={r.id}>{r.name} ({r.effective_rate}%)</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </TableCell>
                            <TableCell>
                              {cell.discounted_price_excl_tax && incl
                                ? <Box>
                                    <Typography variant="caption" color="text.disabled"
                                      sx={{ textDecoration: 'line-through', display: 'block', lineHeight: 1.3 }}>
                                      {incl}
                                    </Typography>
                                    <Typography variant="body2" fontWeight={700} color="success.dark">
                                      {rule
                                        ? (parseFloat(cell.discounted_price_excl_tax) * (1 + rule.effective_rate / 100)).toFixed(2)
                                        : parseFloat(cell.discounted_price_excl_tax).toFixed(2)}
                                    </Typography>
                                  </Box>
                                : incl
                                  ? <Typography variant="body2" fontWeight={600} color="success.dark">{incl}</Typography>
                                  : <Typography variant="body2" color="text.disabled">—</Typography>}
                            </TableCell>
                            <TableCell>
                              <TextField size="small" type="number" placeholder="0"
                                value={cell.bundled_tokens ?? ''}
                                onChange={e => setCell(currency, bp, 'bundled_tokens', +e.target.value)}
                                sx={{ width: 85 }} InputProps={{ inputProps: { min: 0 } }} />
                            </TableCell>
                            <TableCell>
                              {bp === 'one_time'
                                ? <TextField size="small" type="number" placeholder="e.g. 30"
                                    value={cell.duration_days ?? ''}
                                    onChange={e => setCell(currency, bp, 'duration_days', e.target.value || null)}
                                    sx={{ width: 90 }} InputProps={{ inputProps: { min: 1 } }} />
                                : <Typography variant="body2" color="text.disabled">—</Typography>}
                            </TableCell>
                            <TableCell>
                              {bp === 'yearly'
                                ? <TextField size="small" type="number" placeholder="0"
                                    value={cell.yearly_bonus_pct ?? ''}
                                    onChange={e => setCell(currency, bp, 'yearly_bonus_pct', e.target.value)}
                                    sx={{ width: 86 }}
                                    InputProps={{
                                      inputProps: { min: 0, max: 100, step: '0.01' },
                                      endAdornment: <InputAdornment position="end">
                                        <Typography variant="caption" color="text.secondary">%</Typography>
                                      </InputAdornment>,
                                    }} />
                                : <Typography variant="body2" color="text.disabled">—</Typography>}
                            </TableCell>
                            <TableCell>
                              <Switch size="small"
                                checked={bp !== 'one_time' && !!cell.is_auto_renew}
                                disabled={bp === 'one_time'}
                                onChange={e => setCell(currency, bp, 'is_auto_renew', e.target.checked)} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Paper>
              </Box>
            ))
          }
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters elevation={0} sx={ACCORDION_SX}>
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 1.5, minHeight: 52 }}>
          <SectionHeader label="Merchant Module Access" />
        </AccordionSummary>
        <Divider />
        <AccordionDetails sx={{ p: 3 }}>
          {merchantModules.length === 0
            ? <Typography variant="body2" color="text.secondary">No merchant modules found.</Typography>
            : (() => {
                const allActionIds = merchantModules.flatMap(m => m.actions.map(a => a.action_id));
                const allGranted   = allActionIds.length > 0 && allActionIds.every(id => !!planRbac[id]);
                const someGranted  = !allGranted && allActionIds.some(id => !!planRbac[id]);
                const toggleAllRbac = () => {
                  const updates = {};
                  allActionIds.forEach(id => { updates[id] = !allGranted; });
                  setIsDirtyRbac(true);
                  setPlanRbac(p => ({ ...p, ...updates }));
                };
                return (
                  <>
                    {/* Global select-all */}
                    <Box display="flex" alignItems="center" gap={1} mb={2}
                      sx={{ pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Checkbox
                        sx={CB_SX}
                        checked={allGranted}
                        indeterminate={someGranted}
                        onChange={toggleAllRbac}
                      />
                      <Typography variant="body2" fontWeight={600} color="text.secondary">
                        {allGranted ? 'Deselect all permissions' : 'Select all permissions'}
                      </Typography>
                      {someGranted && (
                        <Typography variant="caption" color="text.disabled">
                          ({allActionIds.filter(id => !!planRbac[id]).length} / {allActionIds.length} selected)
                        </Typography>
                      )}
                    </Box>

                    <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                      Merchants see a module only when their role has permission <strong>and</strong> this plan grants it.
                    </Typography>

                    {['core', 'feature'].map(tier => {
                      const tierModules = merchantModules.filter(m => m.module_tier === tier);
                      if (!tierModules.length) return null;
                      return (
                        <Box key={tier} mb={2.5}>
                          <Typography variant="subtitle2" fontWeight={700} color="text.secondary"
                            sx={{ textTransform: 'uppercase', letterSpacing: 0.6, mb: 1.5 }}>
                            {tier === 'core' ? 'Core Modules' : 'Premium Features'}
                          </Typography>
                          <Grid container spacing={2}>
                            {tierModules.map(mod => {
                        const modActionIds = mod.actions.map(a => a.action_id);
                        const modAllGranted  = modActionIds.every(id => !!planRbac[id]);
                        const modSomeGranted = !modAllGranted && modActionIds.some(id => !!planRbac[id]);
                        const toggleMod = () => {
                          const updates = {};
                          modActionIds.forEach(id => { updates[id] = !modAllGranted; });
                          setIsDirtyRbac(true);
                          setPlanRbac(p => ({ ...p, ...updates }));
                        };
                        return (
                          <Grid item xs={12} md={6} key={mod.module_id}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
                              {/* Module header with select-all */}
                              <Box display="flex" alignItems="center" gap={0.5} mb={1}
                                sx={{ pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Checkbox
                                  size="small"
                                  checked={modAllGranted}
                                  indeterminate={modSomeGranted}
                                  onChange={toggleMod}
                                  sx={{ '& .MuiSvgIcon-root': { fontSize: 20 }, p: 0.3 }}
                                />
                                <Typography variant="body2" fontWeight={700}>{mod.module_name}</Typography>
                              </Box>
                              <Box display="flex" flexDirection="column" gap={0.5}>
                                {mod.actions.map(action => (
                                  <Box key={action.action_id} display="flex" alignItems="center" gap={1}
                                    sx={isViewMode && versionDiff.rbacActionIds.has(action.action_id) ? {
                                      bgcolor: 'warning.50', borderRadius: 1,
                                      mx: -0.75, px: 0.75, py: 0.25,
                                      border: '1px solid', borderColor: 'warning.200',
                                    } : {}}>
                                    <FormControlLabel sx={{ mx: 0, flex: 1, minWidth: 0 }}
                                      control={
                                        <Checkbox sx={CB_SX}
                                          checked={!!planRbac[action.action_id]}
                                          onChange={e => {
                                            setIsDirtyRbac(true);
                                            setPlanRbac(p => ({ ...p, [action.action_id]: e.target.checked }));
                                            if (!e.target.checked) setPlanRbacLimits(p => ({ ...p, [action.action_id]: null }));
                                          }} />
                                      }
                                      label={
                                        <Typography variant="body2" color="text.secondary"
                                          sx={{ textTransform: 'capitalize' }}>
                                          {action.action_name.replace(/_/g, ' ')}
                                        </Typography>
                                      } />
                                    <TextField
                                      size="small" type="number" placeholder="∞"
                                      disabled={!planRbac[action.action_id]}
                                      value={planRbacLimits[action.action_id] ?? ''}
                                      onChange={e => {
                                        setIsDirtyRbac(true);
                                        setPlanRbacLimits(p => ({ ...p, [action.action_id]: e.target.value ? parseInt(e.target.value) : null }));
                                        setRbacErrors(p => { const n = {...p}; delete n[action.action_id]; return n; });
                                      }}
                                      inputProps={{ min: 1 }}
                                      error={!!rbacErrors[action.action_id]}
                                      helperText={rbacErrors[action.action_id] || undefined}
                                      sx={{ width: 88, flexShrink: 0, '& .MuiInputBase-input': { py: 0.6, fontSize: 12, textAlign: 'center' } }}
                                    />
                                  </Box>
                                ))}
                              </Box>

                              {mod.route_name === 'generate' && (
                                <Box mt={2} pt={2} sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                                  <Typography variant="caption" fontWeight={700} color="text.secondary"
                                    letterSpacing={0.6} sx={{ textTransform: 'uppercase', display: 'block', mb: 1.5 }}>
                                    Generation Restrictions
                                  </Typography>
                                  <Alert severity="info" sx={{ mb: 2, py: 0.5, fontSize: 12, borderRadius: 1.5 }}>
                                    Unchecked items are hidden on the merchant Create page. Dependent options sync automatically
                                    (e.g. Bust Display needs bust type + color; Human Model/Hand needs ethnicity or clothing).
                                  </Alert>
                                  {renderAllGenerationRestrictions(planRestrictions, updatePlanRestrictions)}
                                </Box>
                              )}
                            </Paper>
                          </Grid>
                        );
                            })}
                          </Grid>
                        </Box>
                      );
                    })}
                  </>
                );
              })()
          }
        </AccordionDetails>
      </Accordion>

      {/* ── Feature Display Order card ─────────────────────────────────── */}
      {!isViewMode && (
        <Accordion defaultExpanded disableGutters elevation={0} sx={ACCORDION_SX}>
          <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 1.5, minHeight: 52 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <SectionHeader label="Feature Display Order" />
              {isDirtyLabels && (
                <Chip label="Unsaved" size="small" color="warning" variant="outlined" sx={{ fontSize: 10, height: 20 }} />
              )}
            </Box>
          </AccordionSummary>
          <Divider />
          <AccordionDetails sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.6 }}>
              These labels appear on the {isAddon ? 'add-on' : 'plan'} card shown to merchants. Drag to reorder, edit the text, or add custom entries not tied to any feature module (e.g. "Priority Support").
            </Typography>

            {featureLabels.length === 0 ? (
              <Box sx={{
                border: '1.5px dashed',
                borderColor: 'divider',
                borderRadius: 2,
                p: '20px 24px',
                textAlign: 'center',
                bgcolor: 'grey.50',
                mb: 2,
              }}>
                <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Button
                    size="small" variant="contained" disableElevation
                    onClick={autoPopulateFeatureLabels}
                  >
                    Auto-populate from features
                  </Button>
                  <Button
                    size="small" variant="outlined" startIcon={<Add />}
                    onClick={() => {
                      setFeatureLabels([{ label: '', module_id: null, sort_order: 0 }]);
                      setIsDirtyLabels(true);
                    }}
                  >
                    Add blank label
                  </Button>
                </Box>
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 2 }}>
                  {featureLabels.map((feat, i) => (
                    <Box
                      key={i}
                      draggable
                      onDragStart={e => {
                        e.dataTransfer.setData('featureLabelIdx', String(i));
                        dragLabelIdx.current = i;
                        e.currentTarget.style.opacity = '0.5';
                      }}
                      onDragEnd={e => { e.currentTarget.style.opacity = ''; }}
                      onDragOver={e => {
                        e.preventDefault();
                        e.currentTarget.style.borderColor = '#6318C6';
                        e.currentTarget.style.background = '#faf5ff';
                      }}
                      onDragLeave={e => {
                        e.currentTarget.style.borderColor = '';
                        e.currentTarget.style.background = '';
                      }}
                      onDrop={e => {
                        e.preventDefault();
                        e.currentTarget.style.borderColor = '';
                        e.currentTarget.style.background = '';
                        const from = parseInt(e.dataTransfer.getData('featureLabelIdx'), 10);
                        if (isNaN(from) || from === i) return;
                        const updated = [...featureLabels];
                        const [moved] = updated.splice(from, 1);
                        updated.splice(i, 0, moved);
                        setFeatureLabels(updated.map((f, idx) => ({ ...f, sort_order: idx })));
                        setIsDirtyLabels(true);
                      }}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        px: 1, py: 0.75,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1.5,
                        bgcolor: 'background.paper',
                        cursor: 'grab',
                        transition: 'box-shadow 0.15s',
                        '&:hover': {
                          boxShadow: '0 2px 8px rgba(99,24,198,0.10)',
                          borderColor: 'primary.light',
                        },
                        '&:active': { cursor: 'grabbing' },
                      }}
                    >
                      <Tooltip title="Drag to reorder">
                        <DragIndicatorIcon sx={{ color: 'text.disabled', flexShrink: 0, fontSize: 20, ml: 0.5 }} />
                      </Tooltip>

                      <TextField
                        size="small"
                        value={feat.label}
                        placeholder="Feature label"
                        onChange={e => {
                          const updated = featureLabels.map((f, j) => j === i ? { ...f, label: e.target.value } : f);
                          setFeatureLabels(updated);
                          setIsDirtyLabels(true);
                        }}
                        sx={{
                          flex: 1,
                          '& .MuiOutlinedInput-root': { fontSize: 13 },
                          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                          '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { border: '1px solid' },
                          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { border: '1px solid' },
                        }}
                        inputProps={{ maxLength: 200 }}
                      />

                      <Chip
                        label={
                          feat.module_id
                            ? (merchantModules.find(m => m.module_id === feat.module_id)?.module_name || `#${feat.module_id}`)
                            : 'custom'
                        }
                        size="small"
                        variant="outlined"
                        sx={{
                          fontSize: 10,
                          height: 22,
                          flexShrink: 0,
                          maxWidth: 110,
                          color: feat.module_id ? 'text.secondary' : 'secondary.main',
                          borderColor: feat.module_id ? 'divider' : 'secondary.light',
                        }}
                      />

                      <Tooltip title="Remove">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setFeatureLabels(featureLabels.filter((_, j) => j !== i).map((f, idx) => ({ ...f, sort_order: idx })));
                            setIsDirtyLabels(true);
                          }}
                          sx={{ color: 'text.disabled', flexShrink: 0, '&:hover': { color: 'error.main' } }}
                        >
                          <Delete sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Button
                    size="small" variant="outlined" startIcon={<Add />}
                    onClick={() => {
                      setFeatureLabels(prev => [...prev, { label: '', module_id: null, sort_order: prev.length }]);
                      setIsDirtyLabels(true);
                    }}
                  >
                    Add Label
                  </Button>
                  <Button
                    size="small" variant="text" color="secondary"
                    onClick={autoPopulateFeatureLabels}
                  >
                    Auto-populate from features
                  </Button>
                </Box>
              </>
            )}
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );

  // ── ADDON EDITOR SECTIONS ──────────────────────────────────────────────

  const renderAddonSections = () => (
    <Box display="flex" flexDirection="column" gap={2}>

      <Accordion defaultExpanded disableGutters elevation={0} sx={ACCORDION_SX}>
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 1.5, minHeight: 52 }}>
          <SectionHeader label="Basics" />
        </AccordionSummary>
        <Divider />
        <AccordionDetails sx={{ p: 3 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Add-on Code" value={addonForm.addon_code}
                disabled={!!editingId}
                error={!!addonErrors.addon_code}
                helperText={addonErrors.addon_code || (editingId ? 'Locked after first save' : 'Auto-filled · a–z, 0–9, underscore')}
                onChange={e => {
                  const cleaned = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
                  setAddonForm(f => ({ ...f, addon_code: cleaned }));
                  setAddonErrors(p => ({ ...p, addon_code: addonCodeError(cleaned) || undefined }));
                }}
                onBlur={() => setAddonErrors(p => ({ ...p, addon_code: addonCodeError(addonForm.addon_code) || undefined }))}
                inputProps={{ maxLength: 100 }}
                sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: 13 } }} />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField fullWidth size="small" label="Display Name *" value={addonForm.display_name}
                error={!!addonErrors.display_name}
                helperText={addonErrors.display_name || `${addonForm.display_name.length}/200`}
                inputProps={{ maxLength: 200 }}
                onChange={e => {
                  const name = e.target.value;
                  setAddonForm(f => ({ ...f, display_name: name,
                    ...(!editingId && { addon_code: nameToCode(name) }) }));
                  setAddonErrors(p => ({ ...p, display_name: planNameError(name) || undefined }));
                }}
                onBlur={() => setAddonErrors(p => ({ ...p, display_name: planNameError(addonForm.display_name) || undefined }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Description" multiline rows={2}
                value={addonForm.description}
                onChange={e => setAddonForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Optional — shown to merchants in the add-on marketplace" />
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select label="Category" value={addonForm.addon_category}
                  onChange={e => setAddonForm(f => ({ ...f, addon_category: e.target.value }))}>
                  <MenuItem value="token_pack">Token Pack</MenuItem>
                  <MenuItem value="feature_unlock">Feature Unlock</MenuItem>
                  <MenuItem value="usage_quota">Usage Quota</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Visibility</InputLabel>
                <Select label="Visibility" value={addonForm.visibility}
                  onChange={e => setAddonForm(f => ({ ...f, visibility: e.target.value }))}>
                  <MenuItem value="public">Public</MenuItem>
                  <MenuItem value="users">Users only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4} md={2}>
              <TextField fullWidth size="small" label="Sort Order" type="number"
                value={addonForm.sort_order}
                onChange={e => setAddonForm(f => ({ ...f, sort_order: +e.target.value }))} />
            </Grid>
          </Grid>
          {addonForm.visibility === 'users' && renderAllowlistPanel()}
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded disableGutters elevation={0} sx={ACCORDION_SX}>
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 1.5, minHeight: 52 }}>
          <SectionHeader label="Pricing" count={addonPricing.length} />
        </AccordionSummary>
        <Divider />
        <AccordionDetails sx={{ p: 0 }}>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: addonForm.addon_category === 'token_pack' ? 860 : 760 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...TH, pl: 2.5, width: 95 }}>Currency</TableCell>
                  <TableCell sx={{ ...TH, width: 160 }}>Billing cycle</TableCell>
                  <TableCell sx={{ ...TH, width: 115 }}>Price / MRP (excl.)</TableCell>
                  <TableCell sx={{ ...TH, width: 115 }}>Sale price (excl.)</TableCell>
                  <TableCell sx={{ ...TH, width: 170 }}>Tax rule</TableCell>
                  <TableCell sx={{ ...TH, width: 100 }}>Incl. tax</TableCell>
                  {addonForm.addon_category === 'token_pack' && (
                    <TableCell sx={{ ...TH, width: 100, color: 'primary.main' }}>Tokens grant</TableCell>
                  )}
                  <TableCell sx={{ ...TH, width: 90 }}>Auto-renew</TableCell>
                  <TableCell sx={{ ...TH, width: 48 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {addonPricing.length === 0
                  ? <TableRow><TableCell colSpan={addonForm.addon_category === 'token_pack' ? 9 : 8} align="center"
                      sx={{ py: 5, fontSize: 13, border: 0,
                        color: addonErrors._pricing ? 'error.main' : 'text.disabled',
                        bgcolor: addonErrors._pricing ? 'error.50' : 'transparent' }}>
                      {addonErrors._pricing
                        ? 'At least one pricing row is required before saving'
                        : 'No pricing rows yet — click "Add Row" below'}
                    </TableCell></TableRow>
                  : addonPricing.map((row, i) => {
                    const addonRule = taxRules.find(r => r.id === row.tax_rule_id);
                    const addonRate = addonRule ? addonRule.effective_rate / 100 : 0;
                    const addonMrpIncl = addonRule && row.price
                      ? (parseFloat(row.price) * (1 + addonRate)).toFixed(2) : null;
                    const addonDiscIncl = addonRule && row.discounted_price_excl_tax
                      ? (parseFloat(row.discounted_price_excl_tax) * (1 + addonRate)).toFixed(2) : null;
                    return (
                    <TableRow key={i}
                      sx={{ '&:last-child td': { borderBottom: 0 }, '&:hover': { bgcolor: 'grey.50' } }}>
                      <TableCell sx={{ pl: 2.5 }}>
                        <FormControl size="small" sx={{ minWidth: 85 }}>
                          <Select value={row.currency_code || ''} displayEmpty
                            onChange={e => {
                              const newCurrency = e.target.value;
                              const cycle = row.billing_cycle || 'one_time';
                              const isDupe = addonPricing.some((r, j) => j !== i && r.currency_code === newCurrency && (r.billing_cycle || 'one_time') === cycle);
                              if (isDupe) { show(`${newCurrency} / ${PERIOD_LABELS[cycle]} already exists`, 'error'); return; }
                              setAddonPricing(p => p.map((r, j) => j === i ? { ...r, currency_code: newCurrency } : r));
                            }}>
                            {currencies.map(c => <MenuItem key={c.code} value={c.code}>{c.code}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          title={row.billing_cycle === 'one_time' ? 'Token expiry = active subscription expiry date' : ''}
                          placement="top" arrow>
                          <FormControl size="small" sx={{ minWidth: 120 }}>
                            <Select value={row.billing_cycle || 'one_time'}
                              onChange={e => {
                                const newCycle = e.target.value;
                                const currency = row.currency_code || '';
                                const isDupe = addonPricing.some((r, j) => j !== i && (r.currency_code || '') === currency && (r.billing_cycle || 'one_time') === newCycle);
                                if (isDupe) { show(`${currency} / ${PERIOD_LABELS[newCycle]} already exists`, 'error'); return; }
                                setAddonPricing(p => p.map((r, j) => j !== i ? r : {
                                  ...r, billing_cycle: newCycle,
                                  is_auto_renew: newCycle === 'one_time' ? false : r.is_auto_renew,
                                }));
                              }}
                              sx={row.billing_cycle === 'one_time' ? { '& .MuiOutlinedInput-notchedOutline': { borderColor: 'warning.main' } } : {}}>
                              {BILLING_PERIODS.map(bp => <MenuItem key={bp} value={bp}>{PERIOD_LABELS[bp]}</MenuItem>)}
                            </Select>
                          </FormControl>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <TextField size="small" type="number" value={row.price ?? ''}
                          onChange={e => setAddonPricing(p => p.map((r, j) => j === i ? { ...r, price: e.target.value } : r))}
                          sx={{ width: 100 }} InputProps={{ inputProps: { min: 0 } }} />
                      </TableCell>
                      <TableCell>
                        <TextField size="small" type="number" placeholder="optional"
                          value={row.discounted_price_excl_tax ?? ''}
                          onChange={e => { setAddonPricing(p => p.map((r, j) => j === i ? { ...r, discounted_price_excl_tax: e.target.value || null } : r)); setAddonErrors(p => { const n={...p}; delete n[`_pricing_${i}`]; return n; }); }}
                          error={!!addonErrors[`_pricing_${i}`]}
                          helperText={addonErrors[`_pricing_${i}`] || ''}
                          sx={{ width: 100 }} InputProps={{ inputProps: { min: 0 } }} />
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 155 }}>
                          <Select displayEmpty value={row.tax_rule_id || ''}
                            onChange={e => setAddonPricing(p => p.map((r, j) => j === i ? { ...r, tax_rule_id: e.target.value || null } : r))}>
                            <MenuItem value=""><em style={{ color: '#999' }}>No tax</em></MenuItem>
                            {taxRules.filter(r => !row.currency_code || r.currency_code === row.currency_code).map(r => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        {addonDiscIncl
                          ? <Box>
                              <Typography variant="caption" color="text.disabled"
                                sx={{ textDecoration: 'line-through', display: 'block', lineHeight: 1.3 }}>
                                {addonMrpIncl}
                              </Typography>
                              <Typography variant="body2" fontWeight={700} color="success.dark">
                                {addonDiscIncl}
                              </Typography>
                            </Box>
                          : addonMrpIncl
                            ? <Typography variant="body2" fontWeight={600} color="success.dark">{addonMrpIncl}</Typography>
                            : <Typography variant="body2" color="text.disabled">—</Typography>}
                      </TableCell>
                      {addonForm.addon_category === 'token_pack' && (
                        <TableCell>
                          <TextField size="small" type="number" value={row.tokens_grant ?? 0}
                            onChange={e => setAddonPricing(p => p.map((r, j) => j === i ? { ...r, tokens_grant: +e.target.value } : r))}
                            sx={{ width: 90 }} InputProps={{ inputProps: { min: 0 } }} />
                        </TableCell>
                      )}
                      <TableCell>
                        <Switch size="small"
                          checked={row.billing_cycle !== 'one_time' && !!row.is_auto_renew}
                          disabled={row.billing_cycle === 'one_time'}
                          onChange={e => setAddonPricing(p => p.map((r, j) => j === i ? { ...r, is_auto_renew: e.target.checked } : r))} />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Remove row">
                          <IconButton size="small"
                            onClick={() => setAddonPricing(p => p.filter((_, j) => j !== i))}
                            sx={{ color: 'error.main', '&:hover': { bgcolor: 'error.50' } }}>
                            <Delete sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                    );
                  })
                }
                <TableRow>
                  <TableCell colSpan={addonForm.addon_category === 'token_pack' ? 9 : 8} sx={{ border: 0, pt: 1, pb: 1.5, pl: 2.5 }}>
                    <Button size="small" startIcon={<Add />}
                      onClick={() => {
                        const defaultCurrency = currencies[0]?.code || 'INR';
                        const defaultCycle = 'one_time';
                        const isDupe = addonPricing.some(
                          r => r.currency_code === defaultCurrency && (r.billing_cycle || 'one_time') === defaultCycle
                        );
                        if (isDupe) {
                          show(`A ${defaultCurrency} / ${PERIOD_LABELS[defaultCycle]} row already exists. Change the currency or billing cycle on the existing row.`, 'error');
                          return;
                        }
                        setAddonPricing(p => [...p, {
                          currency_code: defaultCurrency,
                          billing_cycle: defaultCycle, price: '', tokens_grant: 0, is_auto_renew: false,
                        }]);
                        setAddonErrors(e => { const n = {...e}; delete n._pricing; return n; });
                      }} sx={{ fontWeight: 600 }}>
                      Add Row
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters elevation={0} sx={ACCORDION_SX}>
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 1.5, minHeight: 52 }}>
          <SectionHeader label="Plan Eligibility" count={addonEligiblePlanIds.length || null} />
        </AccordionSummary>
        <Divider />
        <AccordionDetails sx={{ p: 3 }}>
          {allPlans.length === 0
            ? <Typography variant="body2" color="text.secondary">No plans available. Create a plan first.</Typography>
            : <>
                <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                  Select which subscription plans can use this add-on
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {allPlans.map(plan => {
                    const active = addonEligiblePlanIds.includes(plan.id);
                    return (
                      <Chip key={plan.id} label={plan.display_name} size="small" clickable
                        color={active ? 'primary' : 'default'}
                        variant={active ? 'filled' : 'outlined'}
                        onClick={() => setAddonEligiblePlanIds(p =>
                          active ? p.filter(id => id !== plan.id) : [...p, plan.id]
                        )}
                        sx={{ fontWeight: active ? 700 : 400, fontSize: 12 }} />
                    );
                  })}
                </Box>
              </>
          }
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters elevation={0} sx={ACCORDION_SX}>
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 1.5, minHeight: 52 }}>
          <SectionHeader label="Merchant Module Access" />
        </AccordionSummary>
        <Divider />
        <AccordionDetails sx={{ p: 3 }}>
          {merchantModules.length === 0
            ? <Typography variant="body2" color="text.secondary">No merchant modules found.</Typography>
            : (() => {
                const allActionIds  = merchantModules.flatMap(m => m.actions.map(a => a.action_id));
                const allGranted    = allActionIds.length > 0 && allActionIds.every(id => !!addonRbac[id]);
                const someGranted   = !allGranted && allActionIds.some(id => !!addonRbac[id]);
                const toggleAllRbac = () => {
                  const updates = {};
                  allActionIds.forEach(id => { updates[id] = !allGranted; });
                  setAddonRbac(p => ({ ...p, ...updates }));
                };
                return (
                  <>
                    <Box display="flex" alignItems="center" gap={1} mb={2}
                      sx={{ pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Checkbox
                        sx={CB_SX}
                        checked={allGranted}
                        indeterminate={someGranted}
                        onChange={toggleAllRbac}
                      />
                      <Typography variant="body2" fontWeight={600} color="text.secondary">
                        {allGranted ? 'Deselect all permissions' : 'Select all permissions'}
                      </Typography>
                      {someGranted && (
                        <Typography variant="caption" color="text.disabled">
                          ({allActionIds.filter(id => !!addonRbac[id]).length} / {allActionIds.length} selected)
                        </Typography>
                      )}
                    </Box>
                    {['core', 'feature'].map(tier => {
                      const tierModules = merchantModules.filter(m => m.module_tier === tier);
                      if (!tierModules.length) return null;
                      return (
                        <Box key={tier} mb={2.5}>
                          <Typography variant="subtitle2" fontWeight={700} color="text.secondary"
                            sx={{ textTransform: 'uppercase', letterSpacing: 0.6, mb: 1.5 }}>
                            {tier === 'core' ? 'Core Modules' : 'Premium Features'}
                          </Typography>
                    <Grid container spacing={2}>
                      {tierModules.map(mod => {
                        const modActionIds   = mod.actions.map(a => a.action_id);
                        const modAllGranted  = modActionIds.every(id => !!addonRbac[id]);
                        const modSomeGranted = !modAllGranted && modActionIds.some(id => !!addonRbac[id]);
                        const toggleMod = () => {
                          const updates = {};
                          modActionIds.forEach(id => { updates[id] = !modAllGranted; });
                          setAddonRbac(p => ({ ...p, ...updates }));
                        };
                        return (
                          <Grid item xs={12} md={6} key={mod.module_id}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
                              <Box display="flex" alignItems="center" gap={0.5} mb={1}
                                sx={{ pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Checkbox
                                  size="small"
                                  checked={modAllGranted}
                                  indeterminate={modSomeGranted}
                                  onChange={toggleMod}
                                  sx={{ '& .MuiSvgIcon-root': { fontSize: 20 }, p: 0.3 }}
                                />
                                <Typography variant="body2" fontWeight={700}>{mod.module_name}</Typography>
                              </Box>
                              <Box display="flex" flexDirection="column" gap={0.5}>
                                {mod.actions.map(action => (
                                  <Box key={action.action_id} display="flex" alignItems="center" gap={1}>
                                    <FormControlLabel sx={{ mx: 0, flex: 1, minWidth: 0 }}
                                      control={
                                        <Checkbox sx={CB_SX}
                                          checked={!!addonRbac[action.action_id]}
                                          onChange={e => {
                                            setAddonRbac(p => ({ ...p, [action.action_id]: e.target.checked }));
                                            if (!e.target.checked) setAddonRbacLimits(p => ({ ...p, [action.action_id]: null }));
                                          }} />
                                      }
                                      label={
                                        <Typography variant="body2" color="text.secondary"
                                          sx={{ textTransform: 'capitalize' }}>
                                          {action.action_name.replace(/_/g, ' ')}
                                        </Typography>
                                      } />
                                    <TextField
                                      size="small" type="number" placeholder="∞"
                                      disabled={!addonRbac[action.action_id]}
                                      value={addonRbacLimits[action.action_id] ?? ''}
                                      onChange={e => setAddonRbacLimits(p => ({ ...p, [action.action_id]: e.target.value ? parseInt(e.target.value) : null }))}
                                      inputProps={{ min: 1 }}
                                      sx={{ width: 88, flexShrink: 0, '& .MuiInputBase-input': { py: 0.6, fontSize: 12, textAlign: 'center' } }}
                                    />
                                  </Box>
                                ))}
                              </Box>

                              {mod.route_name === 'generate' && (
                                <Box mt={2} pt={2} sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                                  <Typography variant="caption" fontWeight={700} color="text.secondary"
                                    letterSpacing={0.6} sx={{ textTransform: 'uppercase', display: 'block', mb: 1.5 }}>
                                    Generation Restrictions
                                  </Typography>
                                  <Alert severity="info" sx={{ mb: 2, py: 0.5, fontSize: 12, borderRadius: 1.5 }}>
                                    Unchecked items are hidden on the merchant Create page. Dependent options sync automatically
                                    (e.g. Bust Display needs bust type + color; Human Model/Hand needs ethnicity or clothing).
                                  </Alert>
                                  {renderAllGenerationRestrictions(addonRestrictions, updateAddonRestrictions)}
                                </Box>
                              )}
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                        </Box>
                      );
                    })}
                  </>
                );
              })()
          }
        </AccordionDetails>
      </Accordion>

    </Box>
  );

  // ── EDITOR SHELL ───────────────────────────────────────────────────────

  const renderEditor = () => {
    if (editorLoading) return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" gap={1.5} alignItems="center">
            <Skeleton variant="circular" width={34} height={34} />
            <Box><Skeleton width={160} height={28} /><Skeleton width={90} height={18} sx={{ mt: 0.5 }} /></Box>
          </Box>
          <Box display="flex" gap={1}>
            <Skeleton width={120} height={40} sx={{ borderRadius: 2 }} />
            <Skeleton width={120} height={40} sx={{ borderRadius: 2 }} />
          </Box>
        </Box>
        {!editingId && <Skeleton width={220} height={40} sx={{ borderRadius: 2, mb: 3 }} />}
        {[1, 2, 3, 4].map(i => <Skeleton key={i} height={64} sx={{ borderRadius: 2, mb: 2 }} />)}
      </Box>
    );

    const noActivePricing = !Object.values(pricingGrid)
      .flatMap(periods => Object.values(periods))
      .some(row => row.is_period_active);

    return (
      <Box>
        {/* Sticky top bar */}
        <Box display="flex" justifyContent="space-between" alignItems="center"
          sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: 'background.paper',
            borderBottom: '1px solid', borderColor: 'divider',
            py: 1.5, mb: 3, mx: -3, px: 3,
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Tooltip title="Back to list">
              <IconButton
                onClick={() => {
                  if (!isViewMode) { setBackConfirm(true); }
                  else { setView('list'); setIsViewMode(false); setAllVersions([]); }
                }}
                size="small"
                sx={{ border: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'grey.100' } }}>
                <ArrowBack fontSize="small" />
              </IconButton>
            </Tooltip>
            <Box>
              <Typography variant="h6" fontWeight={800} lineHeight={1.2} letterSpacing={-0.3}>
                {isViewMode
                  ? (isAddon ? 'View Add-on' : 'View Plan')
                  : (editingId
                    ? (isAddon ? 'Edit Add-on' : 'Edit Plan')
                    : (isAddon ? 'New Add-on' : 'New Plan'))}
              </Typography>
              {/* Edit mode: show current draft/published chips */}
              {!isViewMode && !isAddon && (draftVersion || publishedVersion) && (
                <Box display="flex" gap={0.5} mt={0.4}>
                  {publishedVersion && (
                    <Chip size="small" label={`Published v${publishedVersion.version_number}`}
                      sx={{ height: 18, fontSize: 10, fontWeight: 700,
                        bgcolor: 'primary.50', color: 'primary.dark',
                        border: '1px solid', borderColor: 'primary.200', '& .MuiChip-label': { px: 1 } }} />
                  )}
                  {draftVersion && (
                    <Chip size="small" label={`Draft v${draftVersion.version_number}`}
                      sx={{ height: 18, fontSize: 10, fontWeight: 700,
                        bgcolor: 'warning.50', color: 'warning.dark',
                        border: '1px solid', borderColor: 'warning.200', '& .MuiChip-label': { px: 1 } }} />
                  )}
                </Box>
              )}
              {/* View mode: version history tabs */}
              {isViewMode && !isAddon && allVersions.length > 0 && (
                <Box display="flex" alignItems="center" gap={0.5} mt={0.5} flexWrap="wrap">
                  {allVersions.map((v, idx) => {
                    const isSelected = idx === selectedViewVersionIdx;
                    const COLOR = v.version_status === 'published'
                      ? { bg: 'primary.50',  color: 'primary.dark',    border: 'primary.200'  }
                      : v.version_status === 'draft'
                        ? { bg: 'warning.50', color: 'warning.dark',    border: 'warning.200'  }
                        : { bg: 'grey.100',   color: 'text.secondary',  border: 'grey.300'     };
                    return (
                      <Chip key={v.id} size="small" clickable
                        label={`v${v.version_number} · ${v.version_status}`}
                        onClick={() => switchViewVersion(idx)}
                        sx={{ height: 22, fontSize: 10, fontWeight: 700, cursor: 'pointer',
                          bgcolor: isSelected ? COLOR.bg : 'background.paper',
                          color: isSelected ? COLOR.color : 'text.disabled',
                          border: '1px solid',
                          borderColor: isSelected ? COLOR.border : 'divider',
                          '& .MuiChip-label': { px: 1 },
                          '&:hover': { bgcolor: COLOR.bg, color: COLOR.color, borderColor: COLOR.border } }} />
                    );
                  })}
                  {/* G17: compare mode toggle */}
                  <Box sx={{ display: 'inline-flex', ml: 1, borderRadius: 1, overflow: 'hidden',
                    border: '1px solid', borderColor: 'divider' }}>
                    {['previous', 'published'].map(mode => (
                      <Box key={mode} onClick={() => {
                          setDiffMode(mode);
                          const v = allVersions[selectedViewVersionIdx];
                          if (!v) return;
                          const publishedV = allVersions.find(av => av.version_status === 'published') || null;
                          const previousV  = allVersions[selectedViewVersionIdx + 1] || null;
                          setVersionDiff(computeVersionDiff(v, mode === 'published' ? publishedV : previousV));
                        }}
                        sx={{ px: 1, py: 0.25, cursor: 'pointer', fontSize: 10, fontWeight: 700,
                          bgcolor: diffMode === mode ? 'warning.100' : 'background.paper',
                          color: diffMode === mode ? 'warning.dark' : 'text.disabled',
                          '&:hover': { bgcolor: 'warning.50' } }}>
                        vs {mode}
                      </Box>
                    ))}
                  </Box>
                  {versionDiff.vsVersionNumber != null && (
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5,
                      px: 1, py: 0.2, borderRadius: 1, ml: 0.5,
                      bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.200' }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: 'warning.300', flexShrink: 0 }} />
                      <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'warning.dark' }}>
                        vs v{versionDiff.vsVersionNumber}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
          <Box display="flex" gap={1}>
            {isViewMode ? (
              /* View mode: only show Edit button */
              canUpdate && editingId && (
                <Button variant="outlined" startIcon={<Edit />}
                  onClick={switchToEdit}
                  sx={{ borderRadius: 2, fontWeight: 600, minWidth: 100 }}>
                  Edit
                </Button>
              )
            ) : (
              /* Edit / create mode: Save Draft + Publish */
              <>
                {canUpdate && (
                  <Button variant="outlined"
                    startIcon={saving ? <CircularProgress size={14} /> : <Save />}
                    onClick={isAddon ? saveAddon : savePlan} disabled={saving}
                    sx={{ borderRadius: 2, fontWeight: 600, minWidth: 120 }}>
                    {saving ? 'Saving…' : (isAddon ? 'Save' : 'Save Draft')}
                  </Button>
                )}
                {!isAddon && canPublish && editingId && (
                  <>
                    <Tooltip title={noActivePricing ? 'Add at least one active pricing row before publishing' : ''} arrow>
                      <span>
                        <Button variant="contained" endIcon={<ExpandMore />}
                          onClick={e => setPublishAnchor(e.currentTarget)}
                          disabled={publishing || noActivePricing}
                          sx={{ borderRadius: 2, fontWeight: 700, minWidth: 120,
                            boxShadow: noActivePricing ? 'none' : '0 2px 8px rgba(0,0,0,0.15)' }}>
                          Publish
                        </Button>
                      </span>
                    </Tooltip>
                    <Popover open={Boolean(publishAnchor)} anchorEl={publishAnchor}
                      onClose={() => setPublishAnchor(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                      PaperProps={{ sx: { width: 340, p: 3, borderRadius: 3,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}>
                      <Typography variant="subtitle1" fontWeight={800} mb={!isDirtyPricing && !isDirtyRbac && editingId ? 1 : 2}>
                        Who gets this version?
                      </Typography>
                      {!isDirtyPricing && !isDirtyRbac && editingId && (
                        <Alert severity="warning" sx={{ mb: 2, fontSize: 12, py: 0.5 }}>
                          No pricing or RBAC changes detected. Publishing will create an identical version.
                        </Alert>
                      )}
                      {[
                        { v: 'new_only', label: 'New subscribers only',
                          sub: 'Existing subscribers keep their current version' },
                        { v: 'migrate_all', label: 'Migrate existing subscribers',
                          sub: 'All active subscribers move to this new version' },
                      ].map(({ v, label, sub }) => (
                        <Box key={v} onClick={() => setPublishScope(v)}
                          sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5,
                            p: 1.5, borderRadius: 2, cursor: 'pointer', border: '1px solid',
                            borderColor: publishScope === v ? 'primary.main' : 'divider',
                            bgcolor: publishScope === v ? 'primary.50' : 'transparent',
                            transition: 'all 0.15s',
                            '&:hover': { borderColor: 'primary.light', bgcolor: 'primary.50' } }}>
                          {publishScope === v
                            ? <CheckCircle color="primary" fontSize="small" sx={{ mt: 0.1, flexShrink: 0 }} />
                            : <RadioButtonUnchecked fontSize="small" sx={{ mt: 0.1, color: 'text.disabled', flexShrink: 0 }} />}
                          <Box>
                            <Typography variant="body2" fontWeight={700}>{label}</Typography>
                            <Typography variant="caption" color="text.secondary">{sub}</Typography>
                          </Box>
                        </Box>
                      ))}
                      <TextField fullWidth size="small" label="Notes (optional)" multiline rows={2}
                        value={publishNotes} onChange={e => setPublishNotes(e.target.value)} sx={{ mb: 2 }} />
                      <Box display="flex" justifyContent="flex-end" gap={1}>
                        <Button size="small" onClick={() => setPublishAnchor(null)} sx={{ borderRadius: 2 }}>
                          Cancel
                        </Button>
                        <Button size="small" variant="contained" onClick={publishPlan}
                          disabled={publishing} sx={{ borderRadius: 2, fontWeight: 700 }}>
                          {publishing ? 'Publishing…' : 'Publish Now'}
                        </Button>
                      </Box>
                    </Popover>
                  </>
                )}
              </>
            )}
          </Box>
        </Box>

        {/* Type selector — new items only, hidden in view mode */}
        {!editingId && !isViewMode && (
          <Paper variant="outlined" sx={{ display: 'inline-flex', mb: 3, overflow: 'hidden', borderRadius: 2 }}>
            {[
              { label: 'Subscription plan', value: false },
              { label: 'Add-on',            value: true  },
            ].map(({ label, value }) => (
              <Box key={label}
                onClick={() => { if (isAddon !== value) { setIsAddon(value); resetState(value); } }}
                sx={{ px: 3, py: 1.1, fontSize: '0.875rem',
                  fontWeight: isAddon === value ? 700 : 500, cursor: 'pointer',
                  bgcolor: isAddon === value ? 'primary.main' : 'transparent',
                  color: isAddon === value ? 'primary.contrastText' : 'text.secondary',
                  transition: 'all 0.15s', userSelect: 'none',
                  '&:hover': { bgcolor: isAddon === value ? 'primary.dark' : 'grey.100' } }}>
                {label}
              </Box>
            ))}
          </Paper>
        )}

        {/* Read-only overlay in view mode — blocks all form interaction while keeping accordions expandable */}
        <Box sx={isViewMode ? {
          '& .MuiInputBase-root': { pointerEvents: 'none', bgcolor: 'grey.50' },
          '& .MuiSelect-select': { pointerEvents: 'none', cursor: 'default' },
          '& .MuiButtonBase-root:not(.MuiAccordionSummary-root)': { pointerEvents: 'none' },
          '& .MuiFormControlLabel-root': { pointerEvents: 'none' },
          '& .MuiAutocomplete-root': { pointerEvents: 'none' },
        } : {}}>
          {!isAddon ? renderPlanSections() : renderAddonSections()}
        </Box>

        {/* G11: confirm currency removal when data exists */}
        <Dialog open={Boolean(removeCurrencyConfirm)} onClose={() => setRemoveCurrencyConfirm(null)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 800 }}>Remove {removeCurrencyConfirm} pricing?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              All entered pricing values for {removeCurrencyConfirm} will be deleted.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setRemoveCurrencyConfirm(null)} sx={{ borderRadius: 2 }}>Cancel</Button>
            <Button variant="contained" color="error" onClick={() => {
              const code = removeCurrencyConfirm;
              setRemoveCurrencyConfirm(null);
              const next = { ...pricingGrid }; delete next[code]; setPricingGrid(next);
            }} sx={{ borderRadius: 2, fontWeight: 700 }}>
              Remove
            </Button>
          </DialogActions>
        </Dialog>

        {/* G02: unsaved-changes confirmation on Back */}
        <Dialog open={backConfirm} onClose={() => setBackConfirm(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 800 }}>Leave without saving?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              Any unsaved changes will be lost.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setBackConfirm(false)} sx={{ borderRadius: 2 }}>Stay</Button>
            <Button variant="contained" color="warning" onClick={() => {
              setBackConfirm(false);
              setView('list'); setIsViewMode(false); setAllVersions([]);
            }} sx={{ borderRadius: 2, fontWeight: 700 }}>
              Leave
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  // ── root ───────────────────────────────────────────────────────────────

  if (!canView) return (
    <Alert severity="error" sx={{ borderRadius: 2 }}>
      You don&apos;t have permission to view Plans &amp; Add-ons.
    </Alert>
  );

  const confirmArchive = async () => {
    const { item } = archiveConfirm;
    setArchiveConfirm({ open: false, item: null });
    if (!item) return;
    const isPlan = item._type === 'plan';
    try {
      if (isPlan) { await api.archivePlan(item.id); show('Plan archived'); }
      else        { await api.archiveAddon(item.id); show('Add-on archived'); }
      loadItems();
    } catch (e) { show(e.response?.data?.message || 'Failed to archive', 'error'); }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {view === 'list'  && renderList()}
      {view === 'edit'  && renderEditor()}

      {/* Archive confirmation dialog */}
      <Dialog open={archiveConfirm.open}
        onClose={() => setArchiveConfirm({ open: false, item: null })}
        maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: 'warning.50',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <WarningAmberRounded sx={{ fontSize: 20, color: 'warning.main' }} />
          </Box>
          <Box>
            <Typography fontWeight={700} fontSize={16}>Archive {archiveConfirm.item?._type === 'plan' ? 'Plan' : 'Add-on'}?</Typography>
            <Typography variant="caption" color="text.secondary">This will hide it from the catalog</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>{archiveConfirm.item?.display_name}</strong> will no longer appear in the merchant catalog.
            Active subscribers are not affected.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setArchiveConfirm({ open: false, item: null })}
            sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={confirmArchive}
            startIcon={<Archive />} sx={{ borderRadius: 2, fontWeight: 700 }}>
            Archive
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── View details dialog ─────────────────────────────────────────── */}
      {(() => {
        const d       = viewDialog.data;
        const isPlan  = viewDialog.type === 'plan';
        const closeVD = () => setViewDialog(v => ({ ...v, open: false }));

        /* ── shared sub-components ── */
        const InfoCard = ({ label, value }) => (
          <Box sx={{ bgcolor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '10px', p: 1.75 }}>
            <Typography sx={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
              {label}
            </Typography>
            <Box sx={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{value ?? <span style={{ color: '#9ca3af' }}>—</span>}</Box>
          </Box>
        );

        const SubHead = ({ label }) => (
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.25, mt: 0.5 }}>
            {label}
          </Typography>
        );

        const PricingTable = ({ rows, isAddon }) => (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '8px', mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f9fafb' }}>
                  {(isAddon
                    ? ['Currency', 'Billing Cycle', 'Price (excl. GST)', 'Price (incl. GST)', 'Tokens Grant', 'Auto Renew']
                    : ['Currency', 'Period', 'Price (excl. GST)', 'Price (incl. GST)', 'Bundled Tokens', 'Yearly Bonus', 'Auto Renew']
                  ).map(h => (
                    <TableCell key={h} sx={{ fontSize: 11, fontWeight: 700, color: '#6b7280', py: 1, borderBottom: '1px solid #e5e7eb' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((p, idx) => (
                  <TableRow key={idx} sx={{ '&:last-child td': { border: 0 }, '&:hover': { bgcolor: '#f9fafb' } }}>
                    <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>{p.currency_code}</TableCell>
                    <TableCell sx={{ fontSize: 12, textTransform: 'capitalize' }}>
                      {(isAddon ? p.billing_cycle : p.billing_period)?.replace(/_/g, ' ')}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>
                      ₹ {Number(isAddon ? p.price : p.mrp_excl_tax).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, color: '#6b7280' }}>
                      {(isAddon ? p.mrp_incl_tax : p.mrp_incl_tax)
                        ? `₹ ${Number(isAddon ? p.mrp_incl_tax : p.mrp_incl_tax).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
                        : '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {isAddon
                        ? (p.tokens_grant ? p.tokens_grant.toLocaleString('en-IN') : '—')
                        : (p.bundled_tokens != null ? p.bundled_tokens.toLocaleString('en-IN') : '—')}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {isAddon
                        ? (p.is_auto_renew ? <Chip label="Yes" size="small" color="success" sx={{ fontSize: 10, height: 20 }} /> : 'No')
                        : (p.yearly_bonus_pct ? `${p.yearly_bonus_pct}%` : '—')}
                    </TableCell>
                    {!isAddon && (
                      <TableCell sx={{ fontSize: 12 }}>
                        {p.billing_period === 'one_time'
                          ? <Typography variant="caption" color="text.disabled">N/A</Typography>
                          : (p.is_auto_renew ? <Chip label="Yes" size="small" color="success" sx={{ fontSize: 10, height: 20 }} /> : 'No')}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        );

        const RbacChips = ({ items }) => (
          <Box display="flex" flexWrap="wrap" gap={0.75}>
            {items.filter(r => r.is_granted).map((r, idx) => {
              const name = `${r.module_name || `Module ${r.module_id}`} · ${r.action_name || `Action ${r.action_id}`}`;
              const limit = r.usage_limit != null ? ` (max ${r.usage_limit})` : '';
              return (
                <Chip key={idx} size="small"
                  label={`${name}${limit}`}
                  sx={{ fontSize: 11, bgcolor: '#f5f3ff', color: '#6d28d9', border: '1px solid #ddd6fe' }} />
              );
            })}
          </Box>
        );

        /* ── version tab panel ── */
        const VersionPanel = ({ version, isAddon: va }) => (
          <Box sx={{ pt: 2.5 }}>
            {(version.pricing || []).length > 0 && (
              <Box mb={3}>
                <SubHead label="Pricing" />
                <PricingTable rows={version.pricing} isAddon={va} />
              </Box>
            )}
            {(version.rbac || []).filter(r => r.is_granted).length > 0 && (
              <Box mb={1}>
                <SubHead label="Module Access" />
                <RbacChips items={version.rbac} />
              </Box>
            )}
            {(version.pricing || []).length === 0 &&
              (version.rbac || []).filter(r => r.is_granted).length === 0 && (
              <Typography sx={{ fontSize: 13, color: '#9ca3af' }}>No configuration yet.</Typography>
            )}
          </Box>
        );

        /* ── build tabs for plans ── */
        const tabs = d ? [
          { label: 'Overview', key: 'overview' },
          ...(isPlan && d.published_version ? [{ label: `Published  v${d.published_version.version_number}`, key: 'published' }] : []),
          ...(isPlan && d.draft_version     ? [{ label: `Draft  v${d.draft_version.version_number}`,         key: 'draft'     }] : []),
        ] : [];

        return (
          <Dialog open={viewDialog.open} onClose={closeVD} maxWidth="lg" fullWidth scroll="paper"
            PaperProps={{ sx: { borderRadius: '14px', overflow: 'hidden' } }}>

            {/* Brand gradient top bar */}
            <Box sx={{ height: 3, background: 'linear-gradient(90deg, #6318C6 0%, #F66CAC 55%, #F2CC00 100%)' }} />

            {/* Header */}
            <Box sx={{ px: 3, pt: 2.5, pb: 0 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box flex={1} minWidth={0}>
                  <Box display="flex" alignItems="center" gap={1} mb={0.5} flexWrap="wrap">
                    <Chip
                      label={isPlan ? (d?.product_type || 'Plan') : (d?.addon_category?.replace(/_/g, ' ') || 'Add-on')}
                      size="small"
                      sx={{ fontSize: 10, fontWeight: 700, textTransform: 'capitalize',
                        bgcolor: isPlan ? '#eff6ff' : '#fdf4ff', color: isPlan ? '#1d4ed8' : '#7c3aed',
                        border: '1px solid', borderColor: isPlan ? '#bfdbfe' : '#e9d5ff' }} />
                    {d && (
                      <Chip
                        label={d.status === 1 ? 'Active' : 'Archived'} size="small"
                        sx={{ fontSize: 10, fontWeight: 700,
                          bgcolor: d.status === 1 ? '#f0fdf4' : '#f9fafb',
                          color:   d.status === 1 ? '#15803d' : '#6b7280',
                          border: '1px solid', borderColor: d.status === 1 ? '#bbf7d0' : '#e5e7eb' }} />
                    )}
                  </Box>
                  <Typography variant="h5" fontWeight={800} letterSpacing={-0.5} sx={{ lineHeight: 1.2, mb: 0.5 }}>
                    {viewDialog.loading ? <Skeleton width={280} /> : (d?.display_name || '—')}
                  </Typography>
                  {d && (
                    <Typography sx={{ fontSize: 12, color: '#9ca3af', fontFamily: "'Roboto Mono', monospace",
                      bgcolor: '#f3f4f6', px: 1, py: 0.25, borderRadius: '4px', display: 'inline-block' }}>
                      {isPlan ? d.plan_code : d.addon_code}
                    </Typography>
                  )}
                </Box>
                <IconButton onClick={closeVD} size="small"
                  sx={{ ml: 2, bgcolor: '#f3f4f6', '&:hover': { bgcolor: '#e5e7eb' } }}>
                  <Close sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>

              {/* Tabs */}
              {d && (
                <Tabs value={viewTab} onChange={(_, v) => setViewTab(v)}
                  sx={{ mt: 2, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: 13, minHeight: 40, px: 0, mr: 3 },
                    '& .MuiTabs-indicator': { bgcolor: '#6318C6', height: 2 } }}>
                  {tabs.map((t, idx) => <Tab key={t.key} label={t.label} />)}
                </Tabs>
              )}
            </Box>

            <Divider />

            <DialogContent sx={{ p: 3, bgcolor: '#fafafa' }}>
              {viewDialog.loading && (
                <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                  <CircularProgress sx={{ color: '#6318C6' }} />
                </Box>
              )}

              {d && tabs[viewTab]?.key === 'overview' && (
                <Box>
                  {/* Info grid */}
                  <Grid container spacing={1.5} mb={3}>
                    {isPlan ? (
                      <>
                        <Grid item xs={6} sm={3}><InfoCard label="Product Type"    value={<span style={{ textTransform: 'capitalize' }}>{d.product_type}</span>} /></Grid>
                        <Grid item xs={6} sm={3}><InfoCard label="Sales Model"     value={d.sales_model?.replace('_', ' ')} /></Grid>
                        <Grid item xs={6} sm={3}><InfoCard label="In Catalog"      value={d.show_in_merchant_catalog ? 'Yes' : 'No'} /></Grid>
                        <Grid item xs={6} sm={3}><InfoCard label="Visibility"      value={d.visibility === 'public' ? 'Public' : 'Users only'} /></Grid>
                        <Grid item xs={6} sm={3}><InfoCard label="Sort Order"      value={d.sort_order} /></Grid>
                        <Grid item xs={6} sm={3}><InfoCard label="Plan Tier"       value={d.plan_tier ?? 0} /></Grid>
                        <Grid item xs={6} sm={3}><InfoCard label="Published"       value={d.published_version ? `v${d.published_version.version_number}` : 'Not published'} /></Grid>
                        <Grid item xs={6} sm={3}><InfoCard label="Draft"           value={d.draft_version ? `v${d.draft_version.version_number}` : '—'} /></Grid>
                        <Grid item xs={6} sm={3}><InfoCard label="Created"         value={d.created_at ? new Date(d.created_at).toLocaleDateString('en-IN') : '—'} /></Grid>
                      </>
                    ) : (
                      <>
                        <Grid item xs={6} sm={3}><InfoCard label="Category"        value={(d.addon_category || '').replace(/_/g, ' ')} /></Grid>
                        <Grid item xs={6} sm={3}><InfoCard label="Visibility"      value={d.visibility === 'public' ? 'Public' : 'Users only'} /></Grid>
                        <Grid item xs={6} sm={3}><InfoCard label="Sort Order"      value={d.sort_order} /></Grid>
                        <Grid item xs={6} sm={3}><InfoCard label="Created"         value={d.created_at ? new Date(d.created_at).toLocaleDateString('en-IN') : '—'} /></Grid>
                      </>
                    )}
                  </Grid>

                  {/* Description */}
                  {d.description && (
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: '10px', mb: 3, bgcolor: '#fff' }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.75 }}>Description</Typography>
                      <Typography sx={{ fontSize: 13, color: '#374151', lineHeight: 1.7 }}>{d.description}</Typography>
                    </Paper>
                  )}

                  {/* Add-on: pricing + features + rbac + eligible plans inline on Overview tab */}
                  {!isPlan && (
                    <Box display="flex" flexDirection="column" gap={3}>
                      {(d.pricing || []).length > 0 && (
                        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '10px', bgcolor: '#fff' }}>
                          <SubHead label="Pricing" />
                          <PricingTable rows={d.pricing} isAddon />
                        </Paper>
                      )}
                      {(d.rbac || []).filter(r => r.is_granted).length > 0 && (
                        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '10px', bgcolor: '#fff' }}>
                          <SubHead label="Module Access" />
                          <RbacChips items={d.rbac} />
                        </Paper>
                      )}
                      {(d.eligible_plan_ids || []).length > 0 && (
                        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '10px', bgcolor: '#fff' }}>
                          <SubHead label="Eligible Plans" />
                          <Box display="flex" flexWrap="wrap" gap={0.75}>
                            {d.eligible_plan_ids.map((pid, idx) => (
                              <Chip key={idx} size="small" label={`Plan ${pid}`}
                                sx={{ fontSize: 11, bgcolor: '#fffbeb', color: '#92400e', border: '1px solid #fde68a' }} />
                            ))}
                          </Box>
                        </Paper>
                      )}
                    </Box>
                  )}

                  {/* Allowed merchants */}
                  {d.visibility === 'users' && (
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '10px', mt: 3, bgcolor: '#fff' }}>
                      <SubHead label={`Allowed Merchants (${(d.allowed_merchants || []).length})`} />
                      {(d.allowed_merchants || []).length === 0
                        ? <Typography sx={{ fontSize: 13, color: '#9ca3af' }}>No merchants added yet.</Typography>
                        : (
                          <Box display="flex" flexWrap="wrap" gap={0.75}>
                            {d.allowed_merchants.map((m, idx) => (
                              <Chip key={idx} size="small"
                                label={m.display_name || m.email || `ID ${m.user_id}`}
                                avatar={<Box sx={{ width: 18, height: 18, bgcolor: '#e0e7ff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: '#4338ca', flexShrink: 0 }}>{(m.display_name || m.email || '?')[0].toUpperCase()}</Box>}
                                sx={{ fontSize: 11, pl: 0.25 }} />
                            ))}
                          </Box>
                        )}
                    </Paper>
                  )}
                </Box>
              )}

              {/* Plan version tabs */}
              {d && isPlan && tabs[viewTab]?.key === 'published' && (
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '10px', bgcolor: '#fff' }}>
                  <VersionPanel version={d.published_version} isAddon={false} />
                </Paper>
              )}
              {d && isPlan && tabs[viewTab]?.key === 'draft' && (
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '10px', bgcolor: '#fff' }}>
                  <VersionPanel version={d.draft_version} isAddon={false} />
                </Paper>
              )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e5e7eb', bgcolor: '#fff' }}>
              {canUpdate && d && (
                <Button variant="outlined" onClick={() => { closeVD(); isPlan ? openPlanEdit(d) : openAddonEdit(d); }}
                  sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, mr: 'auto' }}>
                  Edit {isPlan ? 'Plan' : 'Add-on'}
                </Button>
              )}
              <Button onClick={closeVD}
                sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}>
                Close
              </Button>
            </DialogActions>
          </Dialog>
        );
      })()}

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={closeSnack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.severity} onClose={closeSnack} variant="filled"
          sx={{ borderRadius: 2, boxShadow: '0 4px 16px rgba(0,0,0,0.2)' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
